<template>
  <div class="data-list">
    <!-- 筛选卡片 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filters" size="small" class="filter-form">
        <el-form-item>
          <el-input v-model="filters.object_name" placeholder="文件夹名称" clearable />
        </el-form-item>
        <el-form-item>
          <el-input v-model="filters.vin" placeholder="车架VIN" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="filters.type" placeholder="数据类型" clearable>
            <el-option label="自驾数据" :value="0" />
            <el-option label="事件数据" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="problemTagsFilter"
            placeholder="问题分类"
            multiple
            clearable
            collapse-tags
            @change="handleSearch"
          >
            <el-option
              v-for="tag in allProblemTags"
              :key="tag"
              :label="tag"
              :value="tag"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-date-picker
            v-model="filters.record_date"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="projectFilter"
            placeholder="标注项目"
            clearable
            @change="handleSearch"
          >
            <el-option v-for="name in projectOptions" :key="name" :label="name" :value="name" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="datasetStateFilter"
            placeholder="数据状态"
            clearable
            @change="handleSearch"
          >
            <el-option
              v-for="opt in pipelineStateOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 标签筛选区域 -->
      <div class="tag-filter-section">
        <div class="tag-filter-toggle" @click="toggleTagFilter">
          <i :class="showTagFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          <span>{{ showTagFilter ? '收起标签筛选' : '展开标签筛选' }}</span>
          <span v-if="activeTagCount > 0" class="tag-filter-badge">{{ activeTagCount }} 项条件</span>
        </div>
        <div v-show="showTagFilter" class="tag-filter-grid">
          <div v-for="group in tagFilterGroups" :key="group.key" class="tag-filter-item">
            <label class="tag-filter-label">{{ group.label }}</label>
            <el-select
              v-if="group.selection === 'single'"
              v-model="tagFilters[group.key]"
              :placeholder="`选择${group.label}`"
              clearable
              size="small"
              @change="handleTagChange"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
            <el-select
              v-else
              v-model="tagFilters[group.key]"
              :placeholder="`选择${group.label}`"
              multiple
              clearable
              size="small"
              collapse-tags
              @change="handleTagChange"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 分组表格卡片 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>数据分组</span>
        <div class="table-header-right">
          <el-button type="primary" size="small" icon="el-icon-upload2" @click="openUploadDialog">
            上传数据
          </el-button>
          <el-button
            type="warning"
            size="small"
            icon="el-icon-upload"
            @click="openBatchExtractDialog"
          >
            批量生成标注数据<span v-if="selectedRows.length > 0">（{{ selectedRows.length }}）</span>
          </el-button>
          <el-button
            size="small"
            icon="el-icon-link"
            :disabled="!lsBaseUrl"
            @click="openLabelStudio"
          >
            前往标注平台
          </el-button>
          <span class="total-count">共 {{ total }} 组</span>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="tableData"
        ref="groupTable"
        row-key="object_name"
        height="100%"
        stripe
        border
        size="small"
        style="width:100%"
        :default-sort="{ prop: 'latest_created_at', order: 'descending' }"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="40" />
        <!-- 展开行：展示文件夹内的所有文件 -->
        <el-table-column type="expand" width="40">
          <template slot-scope="{ row: groupRow }">
            <div class="expand-inner">
              <div class="expand-toolbar">
                <span class="expand-toolbar-count">已选 {{ selectedCount(groupRow) }} 个文件</span>
                <el-button
                  size="mini"
                  type="primary"
                  icon="el-icon-download"
                  :disabled="selectedCount(groupRow) === 0 || downloadingFolder"
                  @click="batchDownload(groupRow)"
                >批量下载</el-button>
                <el-button
                  size="mini"
                  icon="el-icon-link"
                  :disabled="selectedCount(groupRow) === 0"
                  @click="batchCopyLink(groupRow)"
                >批量复制链接</el-button>
              </div>
              <el-table
                :data="pbFiles(groupRow)"
                size="mini"
                stripe
                border
                style="width:100%"
                row-key="file_path"
              >
                <el-table-column width="36" align="center">
                  <template slot="header">
                    <el-checkbox
                      :value="isAllSelected(groupRow)"
                      :indeterminate="isIndeterminate(groupRow)"
                      @change="val => onToggleAll(groupRow, val)"
                    />
                  </template>
                  <template slot-scope="{ row: fileRow }">
                    <el-checkbox
                      :value="isFileSelected(groupRow, fileRow)"
                      @change="val => onToggleFile(groupRow, fileRow, val)"
                    />
                  </template>
                </el-table-column>
                <el-table-column prop="file_path" label="文件路径" min-width="280" show-overflow-tooltip />
                <el-table-column prop="file_name" label="文件名" min-width="180" show-overflow-tooltip />
                <el-table-column label="操作" width="230" align="center">
                  <template slot-scope="{ row: fileRow }">
                    <el-button
                      type="text"
                      icon="el-icon-download"
                      size="mini"
                      title="下载"
                      @click="handleDownload(groupRow.object_name, fileRow.file_path, fileRow.file_name)"
                    />
                    <el-button
                      type="text"
                      icon="el-icon-link"
                      size="mini"
                      title="复制链接"
                      @click="handleCopyLink(groupRow.object_name, fileRow.file_path)"
                    />
                    <!-- 可视化只对 pb 文件有意义 -->
                    <el-button
                      v-if="isPbFile(fileRow.file_name)"
                      type="text"
                      icon="el-icon-view"
                      size="mini"
                      :disabled="visualizeBusy"
                      @click="handleVisualize(fileRow)"
                    >NS可视化</el-button>
                    <el-button
                      v-if="isPbFile(fileRow.file_name)"
                      type="text"
                      icon="el-icon-monitor"
                      size="mini"
                      :disabled="lichtblickBusy"
                      @click="handleLichtblickVisualize(fileRow)"
                    >LB可视化</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="关键帧" width="110" align="center">
          <template slot-scope="{ row }">
            <KeyframeThumbnail
              :first-keyframe-id="row.first_keyframe_id"
              :object-name="row.object_name"
              :record-mode="row.record_mode"
            />
          </template>
        </el-table-column>
        <el-table-column prop="object_name" label="文件夹" width="200" show-overflow-tooltip />
        <el-table-column label="pb 文件数" width="90" align="center">
          <template slot-scope="{ row }">
            <span class="file-count">{{ pbCount(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="vin" label="车架VIN" width="150" show-overflow-tooltip />
        <el-table-column label="环境版本" width="120" show-overflow-tooltip>
          <template slot="header">
            <span>环境版本</span>
            <el-tooltip content="采集时车端环境包的版本（pb 里的 env.version），不是数据集版本" placement="top">
              <i class="el-icon-info header-hint" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <span v-if="row.env_version">{{ row.env_version }}</span>
            <span v-else class="no-label-status">-</span>
          </template>
        </el-table-column>
        <el-table-column label="地图名" width="100" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.map_name">{{ row.map_name }}</span>
            <span v-else class="no-label-status">-</span>
          </template>
        </el-table-column>
        <el-table-column label="数据类型" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.general_count > 0" size="mini" style="margin:0 2px">自驾数据</el-tag>
            <el-tag v-if="row.trigger_count > 0" type="warning" size="mini" style="margin:0 2px">事件数据</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="场景标签">
          <template slot="header">
            <span>场景标签</span>
            <el-tooltip :content="labelWeightTip" placement="top">
              <i class="el-icon-info header-hint" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <div class="label-tags" v-if="row.label_tags && row.label_tags.length > 0">
              <el-tag
                v-for="(tag, idx) in row.label_tags"
                :key="idx"
                size="mini"
              >{{ tag.name }}<el-tooltip :content="labelWeightTip" placement="top"><span class="tag-weight">:{{ Math.round(tag.weight * 100) }}%</span></el-tooltip></el-tag>
            </div>
            <span v-else class="no-label-status">-</span>
          </template>
        </el-table-column>
        <el-table-column label="问题分类" width="120">
          <template slot-scope="{ row }">
            <div class="problem-tags-cell">
              <div class="label-tags" v-if="row.problem_tags && row.problem_tags.length > 0">
                <template v-for="(tag, idx) in row.problem_tags">
                  <!-- 事件名（error name）：hover 用 popover 展示全部文案 -->
                  <el-popover
                    v-if="isTriggerTag(row, tag)"
                    :key="'trigger-' + idx"
                    class="trigger-popover"
                    placement="top"
                    trigger="hover"
                    :content="tag"
                    popper-class="problem-tag-popover"
                  >
                    <el-tag slot="reference" size="mini" type="warning" class="trigger-tag">{{ tag }}</el-tag>
                  </el-popover>
                  <el-tag
                    v-else
                    :key="idx"
                    size="mini"
                    type="warning"
                    :title="tag"
                  >{{ tag }}</el-tag>
                </template>
              </div>
              <el-button
                type="text"
                icon="el-icon-edit"
                size="mini"
                class="edit-tags-btn"
                @click="openEditDialog(row)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="记录日期时间" width="150" align="center" sortable :sort-method="compareRecordTime">
          <template slot-scope="{ row }">{{ formatRecordTime(row) }}</template>
        </el-table-column>
        <el-table-column label="标注状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag
              v-if="annotStateOf(row)"
              :type="annotStateOf(row).tag"
              size="mini"
            >{{ annotStateOf(row).label }}</el-tag>
            <span v-else class="no-label-status">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="340" align="center" fixed="right">
          <template slot-scope="{ row }">
            <div class="op-cell">
              <el-button
                type="text"
                icon="el-icon-download"
                size="small"
                :disabled="downloadingFolder || directDownload.loading"
                @click="handleDownload(row.object_name)"
              >下载</el-button>
              <el-button
                type="text"
                icon="el-icon-view"
                size="small"
                :disabled="visualizeBusy"
                @click="handleFolderVisualize(row)"
              >NS可视化</el-button>
              <el-button
                type="text"
                icon="el-icon-video-play"
                size="small"
                :disabled="lichtblickBusy"
                @click="handleFolderLichtblickVisualize(row)"
              >LB可视化</el-button>
              <!-- 已推送的目录：状态 chip（点击开二级弹窗看三段详情） -->
              <el-button
                v-if="!batchOf(row)"
                type="text"
                icon="el-icon-upload"
                size="small"
                @click="openExtractDialog(row)"
              >生成标注数据</el-button>
              <DatasetPipelineCell
                v-else
                :batch="batchOf(row)"
                @refresh="loadData({ silent: true })"
                @busy="handlePipelineBusy"
              />
              <!-- 危险动作与其余按钮隔开 -->
              <span class="op-divider" />
              <el-button
                type="text"
                icon="el-icon-delete"
                size="small"
                class="danger-text"
                @click="handleDelete(row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 上传数据弹窗 -->
    <el-dialog
      title="上传数据"
      :visible.sync="uploadDialog.visible"
      width="960px"
      :close-on-click-modal="false"
      :before-close="handleUploadDialogClose"
    >
      <div class="upload-dialog-body">
        <!-- 左栏：上传 -->
        <div class="upload-left">
          <!-- 选择文件夹 -->
          <div class="upload-section">
            <div class="upload-section-title">选择本地文件夹</div>
            <div class="upload-section-body">
              <input
                ref="uploadFolderInput"
                type="file"
                webkitdirectory
                directory
                multiple
                class="upload-file-input"
                @change="handleFolderSelected"
              />
              <el-button size="small" @click="$refs.uploadFolderInput.click()">
                <i class="el-icon-folder-add" /> 选择文件夹
              </el-button>
              <span v-if="uploadDialog.folderName" class="upload-folder-name">
                {{ uploadDialog.folderName }}
              </span>
              <span v-else class="upload-file-hint">未选择文件夹</span>

              <div v-if="uploadDialog.folderName && !uploadDialog.folderNameValid" class="upload-folder-error">
                <i class="el-icon-warning" />
                <span v-if="uploadDialog.folderError">{{ uploadDialog.folderError }}</span>
                <span v-else>文件夹名称格式不正确，需要匹配 <code>数字_日期_时间</code>（如 000001_20260729_081626）</span>
              </div>
              <div v-if="uploadDialog.folderName && uploadDialog.folderNameValid" class="upload-folder-ok">
                <i class="el-icon-success" />
                {{ uploadDialog.files.length }} 个文件
                <span v-if="uploadDialog.vin" class="upload-folder-vin">| VIN: {{ uploadDialog.vin }}</span>
              </div>
            </div>
          </div>

          <!-- 文件列表 -->
          <div class="upload-section" v-if="uploadDialog.files.length > 0">
            <div class="upload-section-title">
              文件列表
              <span class="upload-section-count">{{ uploadDialog.files.length }} 个</span>
            </div>
            <div class="upload-file-list">
              <div v-for="(file, idx) in uploadDialog.files" :key="idx" class="upload-file-item">
                <span class="upload-file-name" :title="file.relativePath">{{ file.relativePath }}</span>
                <span class="upload-file-size">{{ formatFileSize(file.size) }}</span>
                <span v-if="file.status === 'pending'" class="upload-file-status pending">等待</span>
                <el-progress
                  v-else-if="file.status === 'uploading'"
                  :percentage="file.progress"
                  :stroke-width="4"
                  style="width:80px"
                />
                <span v-else-if="file.status === 'success'" class="upload-file-status success">已上传</span>
                <span v-else-if="file.status === 'error'" class="upload-file-status error" :title="file.error">失败</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 右栏：问题分类 -->
        <div class="upload-right" v-if="uploadDialog.folderNameValid">
          <div class="upload-section">
            <div class="upload-section-title">问题分类</div>
            <div class="upload-section-body">
              <el-checkbox-group v-model="uploadDialog.problemTags" size="small">
                <div v-for="group in problemCategoryGroups" :key="group.label" class="problem-group">
                  <div class="problem-group-label">{{ group.label }}</div>
                  <div class="problem-group-items">
                    <el-checkbox
                      v-for="tag in group.items"
                      :key="tag"
                      :label="tag"
                      border
                    />
                  </div>
                </div>
              </el-checkbox-group>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer">
        <el-button size="small" @click="handleUploadDialogClose">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :disabled="!canStartUpload"
          :loading="uploadDialog.uploading"
          @click="startUpload"
        >
          {{ uploadDialog.uploading ? '上传中…' : '开始上传' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 生成标注数据弹窗 -->
    <el-dialog
      :title="extractDialog.batchMode ? `批量生成标注数据 — 已选 ${selectedRows.length} 个文件夹` : `生成标注数据 — ${extractDialog.objectName}`"
      :visible.sync="extractDialog.visible"
      width="760px"
      :close-on-click-modal="false"
      @close="stopExtractPolling"
    >
      <div v-if="!extractDialog.progressOnly" class="xf">
        <div class="xf-top">
          <div class="xf-field">
            <span class="xf-label">目标项目</span>
            <el-select
              v-model="extractDialog.projectId"
              size="small"
              placeholder="选择标注平台项目"
              class="xf-project"
            >
              <el-option
                v-for="p in extractDialog.projects"
                :key="p.id"
                :label="`${p.title}（${p.task_number || 0} 条）`"
                :value="p.id"
              />
            </el-select>
            <el-button size="mini" icon="el-icon-plus" :loading="extractDialog.creating" @click="createProject">新建</el-button>
          </div>
          <div class="xf-field">
            <span class="xf-label">目标数据集</span>
            <el-select v-model="extractDialog.datasetId" size="small" placeholder="默认数据集">
              <el-option
                v-for="d in extractDialog.datasets"
                :key="d.id"
                :label="d.name ? `${d.name}（${d.slug}）` : d.slug"
                :value="d.id"
              />
            </el-select>
          </div>
        </div>

        <div v-if="!extractDialog.batchMode" class="xf-section">
          <div class="xf-section-head">
            <span class="xf-step">1</span><b>抽帧范围</b>
            <span class="xf-count">
              已选 {{ extractDialog.selected.length }} / {{ extractDialog.preview.items.length }} 段
            </span>
            <span class="xf-tools">
              <el-button type="text" size="mini" @click="selectFilm('all')">全选</el-button>
              <el-button type="text" size="mini" @click="selectFilm('none')">全不选</el-button>
              <el-button type="text" size="mini" @click="selectFilm('invert')">反选</el-button>
            </span>
          </div>
          <div v-if="extractDialog.preview.loading" class="xf-hint">pb 预览加载中…</div>
          <div v-else-if="extractDialog.preview.error" class="xf-hint xf-error">{{ extractDialog.preview.error }}</div>
          <div v-else class="xf-films">
            <div
              v-for="(item, i) in extractDialog.preview.items"
              :key="item.pb_file_name"
              class="xf-film"
              :class="{ on: isFilmSelected(i) }"
              :title="item.pb_file_name"
              @click="openFilmViewer(i)"
            >
              <img v-if="item.keyframe_id" :src="filmImageSrc(item.keyframe_id)" />
              <div v-else class="xf-film-empty">{{ i + 1 }}</div>
              <span class="xf-film-name">{{ filmLabel(item.pb_file_name, i) }}</span>
              <span v-if="isFilmSelected(i)" class="xf-film-check">✓</span>
            </div>
          </div>
          <div class="xf-hint">点缩略图放大查看；取消勾选＝这段不抽帧</div>
        </div>

        <div class="xf-section">
          <div class="xf-section-head"><span class="xf-step">2</span><b>抽帧参数</b></div>
          <div class="xf-row">
            <span class="xf-label">策略</span>
            <el-radio-group v-model="extractDialog.strategy" size="small">
              <el-radio-button label="interval">固定间隔</el-radio-button>
              <el-radio-button label="grouped">按秒分组</el-radio-button>
            </el-radio-group>
            <template v-if="extractDialog.strategy === 'interval'">
              <span class="xf-label">间隔</span>
              <el-input-number
                v-model="extractDialog.frameStep"
                :min="1"
                :max="1000"
                size="small"
                controls-position="right"
              />
              <span class="xf-hint">帧，每 {{ extractDialog.frameStep }} 帧留 1 张</span>
            </template>
            <template v-else>
              <span class="xf-label">每组</span>
              <el-input-number
                v-model="extractDialog.samplesPerGroup"
                :min="1"
                :max="10"
                size="small"
                controls-position="right"
              />
              <span class="xf-hint">张，每秒取连续 {{ extractDialog.samplesPerGroup }} 张</span>
            </template>
          </div>
          <div class="xf-row">
            <span class="xf-label">镜头</span>
            <el-button
              v-for="c in ['front', 'rear', 'left', 'right']"
              :key="c"
              size="mini"
              :type="extractDialog.cameras.includes(c) ? 'primary' : ''"
              @click="toggleCamera(c)"
            >{{ c }}</el-button>
          </div>
        </div>

        <div class="xf-section">
          <div class="xf-section-head"><span class="xf-step">3</span><b>输出</b></div>
          <div class="xf-row">
            <span class="xf-label">批次名</span>
            <el-input
              v-if="!extractDialog.batchMode"
              v-model="extractDialog.batch"
              size="small"
              placeholder="默认取目录名"
              style="width: 340px"
            />
            <span v-else class="xf-hint">批量模式：各目录用自己的目录名作批次名，抽全部时间段</span>
          </div>
          <div v-if="extractDialog.batchMode" class="xf-hint">本次将处理 {{ selectedRows.length }} 个目录</div>
        </div>
      </div>

      <div v-if="!extractDialog.batchMode && extractDialog.job" class="extract-progress">
        <div class="extract-progress-title">
          {{ extractDialog.jobType === 'preannotate' ? '预标注' : '生成标注数据' }}
          <span class="extract-progress-target">{{ extractDialog.jobTarget }}</span>
          <el-tag :type="jobStatusTag(extractDialog.job.status)" size="mini">
            {{ jobStatusText(extractDialog.job.status) }}
          </el-tag>
        </div>
        <!-- 预标注的进度条只能拿下载计数当分子，推理期停在顶格会误导，改为不显示条 -->
        <el-progress
          v-if="extractDialog.jobType !== 'preannotate'"
          :percentage="extractPercent"
          :status="extractDialog.job.status === 'failed' ? 'exception' : undefined"
        />
        <div class="extract-hint">
          <template v-if="extractDialog.jobType === 'preannotate'">
            已覆盖 {{ extractDialog.job.image_count || 0 }} 张 · {{ preannotateLine(extractDialog.job) }}
          </template>
          <template v-else>
            pb {{ extractDialog.job.pb_done }}/{{ extractDialog.job.pb_total }}
            ，已上传 {{ extractDialog.job.image_count }} 张
            <span v-if="extractDialog.job.task_count">，标注平台登记 {{ extractDialog.job.task_count }} 条</span>
          </template>
        </div>
        <div v-if="extractDialog.job.error_message" class="extract-error">
          {{ extractDialog.job.error_message }}
        </div>
        <div v-if="extractDialog.job.status === 'failed' && extractDialog.progressOnly" class="extract-poll-dead">
          <template v-if="extractDialog.jobType === 'extract'">
            抽帧失败，可回表单重新生成。
            <el-button type="text" size="mini" @click="retryExtractFromProgress">重新生成</el-button>
          </template>
          <template v-else>
            预标注任务失败；到数据列表点该行状态芯片，在弹窗里「重试预标注」补齐。
          </template>
        </div>
      </div>

      <div v-if="extractDialog.pollDead" class="extract-poll-dead">
        进度查询连续失败，已停止轮询（任务仍在后台执行）。
        <el-button type="text" size="mini" @click="requeryExtract">重新查询</el-button>
      </div>

      <div v-if="extractDialog.batchMode && (extractDialog.batchJobs.length || extractDialog.batchFailed.length)" class="extract-progress">
        <div class="extract-progress-title">
          批量生成标注数据
          <span class="extract-progress-target">已提交 {{ extractDialog.batchJobs.length }} 个目录</span>
        </div>
        <el-progress :percentage="extractPercent" />
        <div class="extract-hint">
          已提交 {{ extractDialog.batchJobs.length }} 个任务，失败 {{ extractDialog.batchFailed.length }} 个
        </div>
        <div v-for="item in extractDialog.batchJobs" :key="item.objectName" class="extract-batch-row">
          <span class="extract-batch-name">{{ item.objectName }}</span>
          <el-tag v-if="item.job.status === 'completed'" type="success" size="mini">完成</el-tag>
          <el-tag v-else-if="item.job.status === 'failed'" type="danger" size="mini">失败</el-tag>
          <el-tag v-else-if="item.job.status === 'processing'" size="mini" color="#409EFF" style="color:#fff;border-color:#409EFF">进行中</el-tag>
          <el-tag v-else type="info" size="mini">等待中</el-tag>
          <span class="extract-hint">
            pb {{ item.job.pb_done }}/{{ item.job.pb_total }}，已上传 {{ item.job.image_count }} 张
            <template v-if="item.job.task_count">，登记 {{ item.job.task_count }} 条</template>
          </span>
          <div v-if="item.job.error_message" class="extract-error">{{ item.job.error_message }}</div>
        </div>
        <div v-for="item in extractDialog.batchFailed" :key="'f-' + item.object_name" class="extract-batch-row">
          <span class="extract-batch-name">{{ item.object_name }}</span>
          <el-tag type="danger" size="mini">未提交</el-tag>
          <span class="extract-error">{{ item.message }}</span>
        </div>
      </div>

      <div slot="footer">
        <el-button size="small" @click="closeExtractDialog">关闭</el-button>
        <el-button
          v-if="!extractSubmitHidden && !extractDialog.progressOnly"
          type="primary"
          size="small"
          :loading="extractDialog.submitting"
          :disabled="extractSubmitDisabled"
          @click="submitExtract"
        >
          {{ extractSubmitLabel }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 胶片条放大查看：尺寸贴合关键帧比例，不再整屏铺黑底 -->
    <el-dialog
      :visible.sync="extractDialog.filmViewer.visible"
      :width="filmViewerDialogWidth"
      top="6vh"
      append-to-body
      custom-class="film-viewer-dialog"
      :close-on-click-modal="true"
      @open="bindViewerKeys"
      @closed="unbindViewerKeys"
    >
      <div slot="title" class="fv-head">
        <span class="fv-head-main">抽帧范围预览</span>
        <span v-if="viewerItem" class="fv-head-sub">
          第 {{ viewerItem.index + 1 }} / {{ extractDialog.preview.items.length }} 段 ·
          {{ filmLabel(viewerItem.pb_file_name, viewerItem.index) }}
        </span>
      </div>
      <div class="fv-body">
        <div class="fv-stage" :style="{ height: filmViewerStageHeight }">
          <img
            v-if="viewerItem && viewerItem.keyframe_id"
            :key="viewerItem.keyframe_id"
            :src="filmPreviewSrc(viewerItem.keyframe_id)"
            class="fv-img"
            @load="onFilmImgLoad"
          />
          <div v-else class="fv-empty">该段暂无关键帧</div>
          <button
            v-if="extractDialog.preview.items.length > 1"
            type="button"
            class="fv-nav fv-nav-prev"
            title="上一段（←）"
            @click="viewerPrev"
          ><i class="el-icon-arrow-left" /></button>
          <button
            v-if="extractDialog.preview.items.length > 1"
            type="button"
            class="fv-nav fv-nav-next"
            title="下一段（→）"
            @click="viewerNext"
          ><i class="el-icon-arrow-right" /></button>
          <div class="fv-caption">
            <span class="fv-caption-time">{{ viewerItem ? filmLabel(viewerItem.pb_file_name, viewerItem.index) : '—' }}</span>
            <span class="fv-caption-state" :class="{ on: viewerSelected }">
              {{ viewerSelected ? '已选，会抽帧' : '未选，这段不抽帧' }}
            </span>
          </div>
        </div>

        <div v-if="extractDialog.preview.items.length > 1" class="fv-strip">
          <div
            v-for="(item, i) in extractDialog.preview.items"
            :key="item.pb_file_name"
            class="fv-thumb"
            :class="{ active: i === extractDialog.filmViewer.index, on: isFilmSelected(i) }"
            :title="filmLabel(item.pb_file_name, i)"
            @click="viewerJump(i)"
          >
            <img v-if="item.keyframe_id" :src="filmImageSrc(item.keyframe_id)" />
            <div v-else class="fv-thumb-empty">{{ i + 1 }}</div>
            <span v-if="isFilmSelected(i)" class="fv-thumb-check">✓</span>
          </div>
        </div>

        <div class="fv-toolbar">
          <span class="fv-tip">← → 切换 · 空格 选中/取消 · Esc 关闭</span>
          <div class="fv-actions">
            <el-button
              size="mini"
              :type="viewerSelected ? 'primary' : 'default'"
              :icon="viewerSelected ? 'el-icon-check' : 'el-icon-plus'"
              @click="viewerToggleSelect"
            >{{ viewerSelected ? '取消选中' : '选中此段' }}</el-button>
            <el-button size="mini" @click="viewerSelectAll">全选</el-button>
            <el-button size="mini" @click="viewerClearAll">全部取消</el-button>
            <el-button size="mini" type="primary" @click="closeFilmViewer">
              完成（已选 {{ extractDialog.selected.length }} / {{ extractDialog.preview.items.length }}）
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 编辑问题分类弹窗 -->
    <el-dialog
      :title="`编辑问题分类 — ${editDialog.objectName}`"
      :visible.sync="editDialog.visible"
      width="520px"
      :close-on-click-modal="false"
    >
      <div class="edit-dialog-body">
        <el-checkbox-group v-model="editDialog.problemTags" size="small">
          <div v-for="group in problemCategoryGroups" :key="group.label" class="problem-group">
            <div class="problem-group-label">{{ group.label }}</div>
            <div class="problem-group-items">
              <el-checkbox
                v-for="tag in group.items"
                :key="tag"
                :label="tag"
                border
              />
            </div>
          </div>
        </el-checkbox-group>
      </div>

      <div slot="footer">
        <el-button size="small" @click="editDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="editDialog.saving"
          @click="saveProblemTags"
        >
          保存
        </el-button>
      </div>
    </el-dialog>

    <!-- 数据组下载：两种网络通道统一走直链复制 -->
    <el-dialog
      title="数据组下载"
      :visible.sync="directDownload.visible"
      width="680px"
      custom-class="direct-download-dialog"
      :close-on-click-modal="false"
    >
      <div class="direct-download-intro">
        <div class="direct-download-heading">
          <i class="el-icon-connection" />
          <span>下载地址已生成</span>
        </div>
        <p v-if="directDownload.internal">当前使用内网加速，浏览器将直接连接内网 MinIO，下载数据不经过平台服务器。</p>
        <p v-else>当前使用公网访问，浏览器将通过 HTTPS 直接连接 MinIO，下载数据不经过平台服务器。</p>
        <p class="direct-download-object">数据组：{{ directDownload.objectName }}</p>
      </div>

      <div class="direct-download-label">直接下载地址</div>
      <el-input
        :value="directDownload.url"
        readonly
        class="direct-download-url"
        @focus="$event.target.select()"
      />

      <div class="direct-download-guide">
        <i class="el-icon-info" />
        <span v-if="directDownload.internal">请点击“复制下载地址”，在新标签页的地址栏中粘贴并打开。不要从当前页面直接打开 HTTP 地址，浏览器可能拦截该下载。</span>
        <span v-else>请点击“复制下载地址”，在新标签页的地址栏中粘贴并打开，浏览器将开始下载。</span>
      </div>

      <div slot="footer">
        <el-button size="small" @click="directDownload.visible = false">关闭</el-button>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-document-copy"
          @click="copyDirectDownloadUrl"
        >复制下载地址</el-button>
      </div>
    </el-dialog>

    <!-- 下载进度弹窗（不遮罩，下载期间页面可继续操作） -->
    <zip-download-dialog ref="zipDl" title="文件夹下载" />

  </div>
</template>

<script>
import { fetchDataGroups, fetchDownloadUrl, fetchUploadUrl, deleteObjects, fetchBatchCreate, updateProblemTags, deleteDataGroup, fetchOpenTicket, fetchFolderTicket, fetchFolderSession, fetchNodeStudioDownloadInfo, fetchDownloadManifest, fetchLichtblickVisualizeConfig, fetchStreamUrl } from '../api/data';
import { createDatasetExtract, createDatasetExtractBatch, fetchDatasetJob, fetchLabelStudioProjects, createDatasetProject, fetchDatasetPreview, fetchDatasets } from '../api/dataset';
import { fetchLabelDictionary, getKeyframeImageUrl } from '../api/label';
import { getNetworkMode, resolveNetworkType } from '../utils/network';
import { downloadFromUrl } from '../utils/download';
import dayjs from 'dayjs';
import KeyframeThumbnail from '../components/KeyframeThumbnail.vue';
import DatasetPipelineCell from '../components/DatasetPipelineCell.vue';
import { PIPELINE_STATES, pipelineFilterOptions } from '../utils/labels';
import { preAnnotateStatusText } from '../utils/preannotate';
import ZipDownloadDialog from '../components/ZipDownloadDialog.vue';

/** 文件夹名称格式: <number>_<date>_<time> (如 000011_20260729_081626) */
const FOLDER_NAME_PATTERN = /^\d+_\d{8}_\d{6}$/;

/** 批次行轮询间隔（有在途任务时才跑；列表刷新带批次进度，节奏比弹窗内轮询缓一些） */
const BATCH_ROW_POLL_MS = 5000;
/** 待审核批次轮询间隔：标注员提交后由 webhook/增量同步写回，页面慢一点跟，别按在途的 5s 空转 */
const REVIEW_ROW_POLL_MS = 15000;

/** 问题分类分组 */
const PROBLEM_CATEGORY_GROUPS = [
  {
    label: 'MOT模型',
    items: ['curb识别不准', 'driving space', '融合标定'],
  },
  {
    label: '点云',
    items: ['格栅'],
  },
  {
    label: '地图',
    items: ['地图未就绪', '导航路线错误', '地图边界', 'IMU、定位异常', '导航路线绕远', '地图编辑', '全局路线修改', '导航报错', '导航异常'],
  },
  {
    label: '规划控制',
    items: ['规划路线异常', '参考线未就绪', '无法规划路线', '碰撞&撞边界', '决策控制参数修改', '行驶状态异常', '远离参考线', '规控节点'],
  },
  {
    label: '系统',
    items: ['内存', '节点', 'CPU占用率', '重启、崩溃', '功能缺失', '车辆状态异常', '系统软件'],
  },
  {
    label: '任务调度',
    items: ['车辆异常离线', '发送任务失败', '地图数据无法加载'],
  },
  {
    label: 'UX/UI',
    items: ['大屏功能错误', 'UX、UI'],
  },
  {
    label: '硬件',
    items: ['底盘', '线束', '电控', '硬件、固件'],
  },
  {
    label: '需求',
    items: ['新增需求'],
  },
  {
    label: '无效数据',
    items: ['无效数据'],
  },
];

/** 用于 el-checkbox-group 平铺所有选项 */
const ALL_PROBLEM_TAGS = PROBLEM_CATEGORY_GROUPS.flatMap(g => g.items);

/** 提示词文本 → 派发用多备选数组：按 | 切分、去空、去重、保序；没有有效段回退类别名（与后端同口径） */
function splitPrompts(text, name) {
  const out = [];
  for (const piece of String(text === undefined || text === null ? '' : text).split('|')) {
    const item = piece.trim();
    if (item && !out.includes(item)) out.push(item);
  }
  return out.length ? out : [name];
}

export default {
  name: 'DataList',

  components: { KeyframeThumbnail, DatasetPipelineCell, ZipDownloadDialog },

  data() {
    return {
      loading: false,
      loadInFlight: false, // 列表请求在途（静默刷新防叠：上一轮没回来就跳过本轮）
      visualizeBusy: false, // 可视化防连点（ticket 一次性）
      lichtblickBusy: false, // Lichtblick 可视化防连点
      lichtblickConfig: null, // 可视化入口配置（后端下发的 entryUrl/layoutUrl）
      extractDialog: { // 生成标注数据弹窗
        visible: false,
        batchMode: false, // 批量模式：多目录一次提交
        batchJobs: [], // 批量任务 [{ objectName, job }]
        batchFailed: [], // 批量提交失败的目录 [{ object_name, message }]
        objectName: '',
        datasetId: null, // 目标数据集（null = 默认数据集）
        datasets: [], // 数据集名册（开弹窗时取一次）
        projectId: null,
        projects: [],
        strategy: 'interval',
        frameStep: 10,
        samplesPerGroup: 2,
        cameras: ['front', 'rear', 'left', 'right'],
        batch: '',
        job: null,
        jobType: 'extract', // 进度视图的任务类型：extract＝生成标注数据 / preannotate＝预标注
        jobTarget: '', // 进度视图的目标（批次名）
        progressOnly: false, // 打开时命中在途任务：只渲染进度视图，不回表单
        submitting: false,
        creating: false,
        timer: null,
        pollInFlight: false, // 轮询防重入
        pollFailures: 0, // 连续失败计数（5 次停轮询并提示重新查询）
        pollDead: false, // 轮询停摆：进度视图内显示「重新查询」
        preview: { loading: false, items: [], error: '' }, // 胶片条：每 pb 一张 front 缩略图
        selected: [], // 选中的段下标集合（默认全选）
        filmViewer: { visible: false, index: 0, naturalW: 0, naturalH: 0 }, // 放大查看蒙版（自然尺寸用于贴合图片比例）
      },
      // 刚提交/在跑的抽帧任务记忆：目录名 → { id, type }。关弹窗重开时找回进度
      // （新提交的批次可能还没有 batch_summary，靠这份记忆兜住）
      recentJobs: {},
      downloadingFolder: false, // 文件夹下载防连点
      directDownload: {
        visible: false,
        loading: false,
        internal: true,
        objectName: '',
        url: '',
      },
      batchPollTimer: null, // 批次行轮询（有在途任务时才开）
      reviewPollTimer: null, // 待审核批次轮询（标注状态回流）
      // 行内弹窗（批次详情 / 选图）打开计数。这些弹窗挂在展开行的子组件里，
      // 轮询换掉行数据会把子组件连同弹窗和已选废帧一起卸载，所以打开期间暂停列表刷新
      pipelineBusy: 0,
      tableData: [],
      total: 0,
      selectedRows: [], // 分组表格多选（批量生成标注数据）
      expandedRows: {},
      selectedFiles: {}, // 展开行文件多选：{ object_name: [file, ...] }
      projectOptions: [], // 标注项目下拉选项（来自 /api/data/groups 的全量 distinct）
      projectFilter: '', // 按标注项目筛选（'' = 全部）
      datasetStateFilter: '', // 按标注数据状态筛选（'' = 全部）
      lsBaseUrl: '', // 标注平台根地址（随数据列表下发）
      showTagFilter: false,
      tagGroups: {},       // 标签字典: { scene: { label, selection, items }, ... }
      tagFilters: {        // 当前标签筛选值（与 query 参数绑定）
        scene: '',
        weather: '',
        time: '',
        lighting: '',
        road: '',
        state: '',
        dynamic: [],
        static: [],
        anomaly: [],
      },
      pagination: {
        page: 1,
        pageSize: 20,
      },
      filters: {
        vin: '',
        type: '',
        record_date_range: null,
        bucket_name: '',
        object_name: '',
      },
      problemCategoryGroups: PROBLEM_CATEGORY_GROUPS,
      allProblemTags: ALL_PROBLEM_TAGS,
      problemTagsFilter: [],
      uploadDialog: {
        visible: false,
        folderName: '',
        folderNameValid: false,
        folderError: '',
        vin: '',
        files: [],
        uploading: false,
        cancelling: false,
        problemTags: [],
      },
      editDialog: {
        visible: false,
        objectName: '',
        problemTags: [],
        saving: false,
      },
    };
  },

  computed: {
    /** 标签筛选分组列表（按字典顺序） */
    tagFilterGroups() {
      const order = ['scene', 'weather', 'time', 'lighting', 'road', 'state', 'dynamic', 'static', 'anomaly'];
      return order
        .filter(key => this.tagGroups[key])
        .map(key => ({ key, ...this.tagGroups[key] }));
    },

    /** 标签列百分比含义说明（tooltip 文案） */
    labelWeightTip() {
      return '占比＝该标签在这组数据中出现比例';
    },

    /** 当前激活的标签筛选条件数量 */
    activeTagCount() {
      let count = 0;
      for (const [key, val] of Object.entries(this.tagFilters)) {
        if (Array.isArray(val) && val.length > 0) count++;
        else if (!Array.isArray(val) && val !== '' && val !== null) count++;
      }
      return count;
    },

    /** 标注数据筛选下拉项（六词表，与标注状态列同源） */
    pipelineStateOptions() {
      return pipelineFilterOptions();
    },

    canStartUpload() {
      return (
        this.uploadDialog.folderNameValid
        && this.uploadDialog.vin !== ''
        && this.uploadDialog.files.length > 0
        && !this.uploadDialog.uploading
      );
    },

    /** 生成标注数据进度（单任务按 pb 估算；批量按终态任务占比） */
    extractPercent() {
      const d = this.extractDialog;
      if (d.batchMode) {
        if (d.batchJobs.length === 0) return 0;
        const done = d.batchJobs.filter(i => ['completed', 'failed'].includes(i.job.status)).length;
        return Math.floor((done / d.batchJobs.length) * 100);
      }
      const job = d.job;
      if (!job) return 0;
      if (job.status === 'completed') return 100;
      if (!job.pb_total) return 0;
      return Math.min(99, Math.floor((job.pb_done / job.pb_total) * 100));
    },

    /** 批量任务是否全部到达终态（completed / failed） */
    extractBatchAllTerminal() {
      const jobs = this.extractDialog.batchJobs;
      return jobs.length > 0 && jobs.every(i => ['completed', 'failed'].includes(i.job.status));
    },

    /** 批量是否存在失败项（决定结束后是否保留「重试失败项」入口） */
    extractBatchHasFailed() {
      const d = this.extractDialog;
      return d.batchFailed.length > 0 || d.batchJobs.some(i => i.job.status === 'failed');
    },

    /**
     * 主按钮是否隐藏：已完成的活儿收掉入口（弹窗从「启动器」变「结果页」）；
     * 失败的保留按钮以便重推，跑动中置灰。
     */
    extractSubmitHidden() {
      const d = this.extractDialog;
      if (d.batchMode) return this.extractBatchAllTerminal && !this.extractBatchHasFailed;
      return !!d.job && d.job.status === 'completed';
    },

    /** 主按钮文案：未跑=开始，失败后重推=重新生成 / 重试失败项 */
    extractSubmitLabel() {
      const d = this.extractDialog;
      if (d.batchMode) {
        return this.extractBatchAllTerminal && this.extractBatchHasFailed ? '重试失败项' : '开始批量生成';
      }
      return d.job && d.job.status === 'failed' ? '重新生成' : '开始生成';
    },

    /** 主按钮禁用：任务运行中置灰，避免重复提交 */
    extractSubmitDisabled() {
      const d = this.extractDialog;
      if (d.batchMode) return d.batchJobs.length > 0 && !this.extractBatchAllTerminal;
      return !!d.job && ['pending', 'processing'].includes(d.job.status);
    },

    /** 蒙版当前展示的段 */
    viewerItem() {
      const v = this.extractDialog.filmViewer;
      return this.extractDialog.preview.items[v.index] || null;
    },

    /** 蒙版当前段是否选中 */
    viewerSelected() {
      return this.viewerItem ? this.isFilmSelected(this.viewerItem.index) : false;
    },

    /**
     * 放映区尺寸：按关键帧真实比例收缩（未加载时按 16:9），再受视口限制。
     * 弹窗宽度跟着它走，所以方图不会出现一大片黑底、宽图也不会被压得很小。
     */
    filmViewerFrame() {
      const v = this.extractDialog.filmViewer;
      const vw = (typeof window !== 'undefined' && window.innerWidth) || 1280;
      const vh = (typeof window !== 'undefined' && window.innerHeight) || 800;
      const maxH = Math.min(Math.max(Math.round(vh * 0.56), 260), 620);
      const maxW = Math.min(Math.round(vw * 0.82), 1080);
      const ratio = v.naturalW > 0 && v.naturalH > 0 ? v.naturalW / v.naturalH : 16 / 9;
      let h = maxH;
      let w = Math.round(h * ratio);
      if (w > maxW) {
        w = maxW;
        h = Math.round(w / ratio);
      }
      return { w, h };
    },

    filmViewerStageHeight() {
      return `${this.filmViewerFrame.h}px`;
    },

    /** 左右各留 20px 内边距，放映区正好等于上面算出来的宽度 */
    filmViewerDialogWidth() {
      return `${this.filmViewerFrame.w + 40}px`;
    },

    /**
     * 当前页有没有批次在途（推送 / 预标注跑着）——只看 job 在途的行，5 秒轮询。
     * 待审核批次另走 hasBatchAwaiting 的 15 秒轮询；两者都停下才不刷新列表。
     */
    hasBatchInFlight() {
      const inFlight = job => !!job && (job.status === 'pending' || job.status === 'processing');
      return this.tableData.some(row => {
        const b = this.batchOf(row);
        return !!b && (inFlight(b.job) || inFlight(b.preannotate_job));
      });
    },

    /** 当前页有没有待审核批次：标注员提交后状态会变，需要页面自己回流 */
    hasBatchAwaiting() {
      return this.tableData.some(row => {
        const b = this.batchOf(row);
        return !!b && b.stage === 'awaiting';
      });
    },

    /**
     * 用户正在弹窗里干活（填表单 / 选图 / 看进度）：此时不刷新列表。
     * 行内弹窗挂在展开行的子组件上，整表换数据会把它们一起卸载。
     */
    pollHold() {
      return this.pipelineBusy > 0
        || this.extractDialog.visible
        || this.extractDialog.filmViewer.visible
        || this.uploadDialog.visible
        || this.editDialog.visible;
    },
  },

  watch: {
    /** 在途批次起止即开关轮询：进度自己走，用户不用手点刷新 */
    hasBatchInFlight(on) {
      if (on) {
        this.stopReviewRowPolling(); // 在途的 5 秒轮询已覆盖，别两套一起跑
        this.startBatchRowPolling();
      } else {
        this.stopBatchRowPolling();
        this.maybeStartReviewRowPolling();
      }
    },
    /** 待审核批次：15 秒重取一次列表，审核提交后自动回流（不用再手点「刷新状态」） */
    hasBatchAwaiting(on) {
      if (on) this.maybeStartReviewRowPolling();
      else this.stopReviewRowPolling();
    },
  },

  mounted() {
    this.loadDictionary();
    // 飞书通知深链：URL 携带 object_name 时直接定位该目录（搜索框同步显示）
    const deepLinkObjectName = this.$route.query.object_name;
    if (deepLinkObjectName) {
      this.filters.object_name = deepLinkObjectName;
    }
    this.loadData();
    this.loadLichtblickConfig();
  },

  beforeDestroy() {
    this.stopExtractPolling();
    this.stopBatchRowPolling();
    this.stopReviewRowPolling();
    this.unbindViewerKeys();
  },

  methods: {
    async openExtractDialog(row) {
      const d = this.extractDialog;
      d.batchMode = false;
      d.batchJobs = [];
      d.batchFailed = [];
      d.objectName = row.object_name;
      d.batch = row.object_name.split('/').pop();
      d.job = null;
      d.jobType = 'extract';
      d.jobTarget = d.batch;
      d.progressOnly = false;
      d.pollDead = false;
      d.visible = true;
      // 在途任务（抽帧/预标注）直接进进度视图接着看：关弹窗不丢，重开就在这里
      const b = this.batchOf(row) || {};
      const inflight = [b.job, b.preannotate_job].find(
        j => j && j.id && (j.status === 'pending' || j.status === 'processing'),
      );
      if (inflight) {
        d.job = inflight;
        d.jobType = b.preannotate_job === inflight ? 'preannotate' : 'extract';
        d.progressOnly = true;
        this.startExtractPolling(inflight.id);
        return;
      }
      // 新提交的批次可能还没有 batch_summary，用本次会话提交时记下的 job 兜底
      const remembered = this.recentJobs[d.objectName];
      if (remembered) {
        try {
          const job = (await fetchDatasetJob(remembered.id)).data;
          if (job.status === 'pending' || job.status === 'processing') {
            d.job = job;
            d.progressOnly = true;
            this.startExtractPolling(job.id);
            return;
          }
          delete this.recentJobs[d.objectName]; // 已到终态：按普通打开走
        } catch {
          // 查不到就按普通打开走
        }
      }
      this.loadExtractPreview(row.object_name);
      this.loadDatasetOptions();
      if (d.projects.length === 0) {
        try {
          // 拦截器返回的是 {code, message, data} 信封，取 data
          d.projects = (await fetchLabelStudioProjects()).data;
        } catch {
          // 错误已由拦截器统一提示
        }
      }
    },

    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },

    /** 批量生成标注数据（表格多选行入口） */
    async openBatchExtractDialog() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先勾选数据分组');
        return;
      }
      const d = this.extractDialog;
      d.batchMode = true;
      d.objectName = '';
      d.batch = '';
      d.job = null;
      d.batchJobs = [];
      d.batchFailed = [];
      d.visible = true;
      this.loadDatasetOptions();
      if (d.projects.length === 0) {
        try {
          d.projects = (await fetchLabelStudioProjects()).data;
        } catch {
          // 错误已由拦截器统一提示
        }
      }
    },

    /** 数据集名册：开弹窗时取一次，默认选中配置里的默认数据集（没取到就不传，后端按默认走） */
    async loadDatasetOptions() {
      try {
        const data = (await fetchDatasets()).data || {};
        const d = this.extractDialog;
        d.datasets = data.items || [];
        if (d.datasetId === null) {
          const fallback = d.datasets.find(item => item.slug === data.default_slug);
          d.datasetId = fallback ? fallback.id : null;
        }
      } catch {
        // 名册拿不到不挡抽帧：不带 dataset_id，后端按默认数据集归集
      }
    },











    /** 胶片条批量选择：all 全选 / none 全不选 / invert 反选 */
    selectFilm(mode) {
      const items = this.extractDialog.preview.items;
      if (mode === 'all') {
        this.extractDialog.selected = items.map((_, i) => i);
        return;
      }
      if (mode === 'none') {
        this.extractDialog.selected = [];
        return;
      }
      const picked = new Set(this.extractDialog.selected);
      this.extractDialog.selected = items.map((_, i) => i).filter(i => !picked.has(i));
    },

    /** 输出镜头按钮多选（至少留一路，提交时另有校验） */
    toggleCamera(camera) {
      const list = this.extractDialog.cameras;
      const at = list.indexOf(camera);
      if (at >= 0) list.splice(at, 1);
      else list.push(camera);
    },

    /** 加载胶片条预览（每 pb 一张 front 缩略图），默认全选 */
    async loadExtractPreview(objectName) {
      const p = this.extractDialog.preview;
      p.loading = true;
      p.error = '';
      p.items = [];
      this.extractDialog.selected = [];
      try {
        const res = await fetchDatasetPreview(objectName);
        p.items = ((res.data && res.data.keyframes) || []).map((k, i) => ({ ...k, index: i }));
        this.extractDialog.selected = p.items.map((_, i) => i);
      } catch {
        p.error = '预览加载失败，可不选时间段直接生成';
      } finally {
        p.loading = false;
      }
    },

    /** 点击缩略图：打开放大查看该段 */
    openFilmViewer(i) {
      const v = this.extractDialog.filmViewer;
      v.index = i;
      v.naturalW = 0;
      v.naturalH = 0;
      v.visible = true;
    },

    /** 大图加载完记下真实比例：弹窗按它收缩，避免整屏黑边 */
    onFilmImgLoad(e) {
      const img = e && e.target;
      if (!img || !img.naturalWidth) return;
      const v = this.extractDialog.filmViewer;
      v.naturalW = img.naturalWidth;
      v.naturalH = img.naturalHeight;
    },

    /** 胶片条内直接跳段 */
    viewerJump(i) {
      this.extractDialog.filmViewer.index = i;
    },

    closeFilmViewer() {
      this.extractDialog.filmViewer.visible = false;
    },

    /** 键盘：← → 切换、空格选中/取消（只在蒙版打开时挂，避免和页面其它快捷键打架） */
    bindViewerKeys() {
      if (this._viewerKeyHandler) return;
      this._viewerKeyHandler = e => {
        const tag = (e.target && e.target.tagName) || '';
        if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.viewerPrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.viewerNext();
        } else if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.viewerToggleSelect();
        }
      };
      window.addEventListener('keydown', this._viewerKeyHandler);
    },

    unbindViewerKeys() {
      if (!this._viewerKeyHandler) return;
      window.removeEventListener('keydown', this._viewerKeyHandler);
      this._viewerKeyHandler = null;
    },

    /** 蒙版上一段 / 下一段（循环） */
    viewerPrev() {
      const n = this.extractDialog.preview.items.length;
      if (n > 0) this.extractDialog.filmViewer.index = (this.extractDialog.filmViewer.index - 1 + n) % n;
    },

    viewerNext() {
      const n = this.extractDialog.preview.items.length;
      if (n > 0) this.extractDialog.filmViewer.index = (this.extractDialog.filmViewer.index + 1) % n;
    },

    viewerToggleSelect() {
      if (this.viewerItem) this.toggleFilmSelected(this.viewerItem.index);
    },

    viewerSelectAll() {
      this.extractDialog.selected = this.extractDialog.preview.items.map((_, i) => i);
    },

    viewerClearAll() {
      this.extractDialog.selected = [];
    },

    isFilmSelected(i) {
      return this.extractDialog.selected.includes(i);
    },

    toggleFilmSelected(i) {
      const sel = this.extractDialog.selected;
      this.extractDialog.selected = sel.includes(i)
        ? sel.filter(x => x !== i)
        : [...sel, i];
    },

    /** 缩略图标签：pb 文件名里的录制时间，解析失败退回段序号 */
    filmLabel(pbFileName, i) {
      const m = /(\d{8})_(\d{6})/.exec(pbFileName);
      return m ? `${m[2].slice(0, 2)}:${m[2].slice(2, 4)}:${m[2].slice(4, 6)}` : `第 ${i + 1} 段`;
    },

    /** 胶片条缩略图地址（后端 resize） */
    filmImageSrc(keyframeId) {
      return `${getKeyframeImageUrl(keyframeId)}?w=240`;
    },

    /** 放大预览原图地址 */
    filmPreviewSrc(keyframeId) {
      return getKeyframeImageUrl(keyframeId);
    },

    /** 新建标注项目（后端带标签模板 + 自动配图源），建完自动选中 */
    async createProject() {
      let title = '';
      try {
        const { value } = await this.$prompt('新标注项目名称', '新建项目', {
          confirmButtonText: '创建',
          cancelButtonText: '取消',
          inputPlaceholder: '例如：仓库-9月新增',
        });
        title = (value || '').trim();
      } catch {
        return; // 用户取消
      }
      if (!title) {
        this.$message.warning('请输入项目名');
        return;
      }
      this.extractDialog.creating = true;
      try {
        const p = (await createDatasetProject(title)).data;
        this.$message.success(`已创建「${p.title}」`);
        this.extractDialog.projects = (await fetchLabelStudioProjects()).data;
        this.extractDialog.projectId = p.id;
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.extractDialog.creating = false;
      }
    },

    closeExtractDialog() {
      this.stopExtractPolling();
      this.extractDialog.visible = false;
    },

    async submitExtract() {
      if (this.extractDialog.batchMode) {
        await this.submitBatchExtract();
        return;
      }
      const d = this.extractDialog;
      if (!d.projectId) {
        this.$message.warning('请选择目标项目');
        return;
      }
      if (d.cameras.length === 0) {
        this.$message.warning('至少要选一路相机');
        return;
      }
      d.submitting = true;
      try {
        if (d.preview.items.length > 0 && d.selected.length === 0) {
          this.$message.warning('请至少选择一段，或点「全选」');
          return;
        }
        const payload = {
          object_name: d.objectName,
          project_id: d.projectId,
          dataset_id: d.datasetId || undefined,
          strategy: d.strategy,
          frame_step: d.frameStep,
          samples_per_group: d.samplesPerGroup,
          cameras: d.cameras,
          batch: d.batch || undefined,
          pb_indexes: d.preview.items.length > 0 ? d.selected : undefined,
        };
        const job = (await createDatasetExtract(payload)).data;
        d.job = job;
        this.recentJobs[d.objectName] = { id: job.id, type: 'extract' };
        this.$message.success('已提交，正在后台处理');
        this.startExtractPolling(job.id);
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        d.submitting = false;
      }
    },

    /** 批量提交：每个目录一个 job（批次名取各文件夹名）；重试时只重推上一轮失败的目录 */
    async submitBatchExtract() {
      const d = this.extractDialog;
      if (!d.projectId) {
        this.$message.warning('请选择目标项目');
        return;
      }
      if (d.cameras.length === 0) {
        this.$message.warning('至少要选一路相机');
        return;
      }
      // 已有上一轮结果时只重推失败的目录，避免把已成功的再推一遍撞去重
      const objectNames = d.batchJobs.length > 0
        ? [
          ...d.batchFailed.map(i => i.object_name),
          ...d.batchJobs.filter(i => i.job.status === 'failed').map(i => i.objectName),
        ]
        : this.selectedRows.map(r => r.object_name);
      if (objectNames.length === 0) {
        this.$message.warning('请先勾选数据分组');
        return;
      }
      d.submitting = true;
      try {
        const payload = {
          object_names: objectNames,
          project_id: d.projectId,
          dataset_id: d.datasetId || undefined,
          strategy: d.strategy,
          frame_step: d.frameStep,
          samples_per_group: d.samplesPerGroup,
          cameras: d.cameras,
        };
        const res = await createDatasetExtractBatch(payload);
        d.batchJobs = (res.data.jobs || []).map(j => ({ objectName: j.object_name, job: j }));
        d.batchFailed = res.data.failed || [];
        d.batchJobs.forEach(item => {
          this.recentJobs[item.objectName] = { id: item.job.id, type: 'extract' };
        });
        this.$message.success(
          `已提交 ${d.batchJobs.length} 个任务，后台处理中${d.batchFailed.length ? `，${d.batchFailed.length} 个失败` : ''}`,
        );
        if (d.batchJobs.length > 0) this.startBatchPolling(d.batchJobs);
        if (this.$refs.groupTable) this.$refs.groupTable.clearSelection();
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        d.submitting = false;
      }
    },

    /** 批量任务轮询：全部到达终态或连续失败 5 次才停（失败落 pollDead，可由「重新查询」恢复） */
    startBatchPolling(jobs) {
      this.stopExtractPolling();
      const d = this.extractDialog;
      d.pollInFlight = false;
      d.pollFailures = 0;
      d.pollDead = false;
      d.timer = setInterval(() => this.pollBatchOnce(jobs), 3000);
    },

    /** 查一轮批量任务（「重新查询」复用；防重入 + 隐藏页跳过） */
    async pollBatchOnce(jobs) {
      const d = this.extractDialog;
      if (d.pollInFlight || document.hidden) return;
      d.pollInFlight = true;
      try {
        for (const item of jobs) {
          if (['completed', 'failed'].includes(item.job.status)) continue;
          item.job = (await fetchDatasetJob(item.job.id)).data;
          if (['completed', 'failed'].includes(item.job.status)) {
            const remembered = this.recentJobs[item.objectName];
            if (remembered && remembered.id === item.job.id) delete this.recentJobs[item.objectName];
          }
        }
        d.pollFailures = 0;
        if (jobs.every(item => ['completed', 'failed'].includes(item.job.status))) {
          this.stopExtractPolling();
          this.loadData({ silent: true }); // 轮询收尾：静默对齐列表，不打断弹窗里的操作
          const ok = jobs.filter(item => item.job.status === 'completed').length;
          this.$message.success(`批量任务全部结束：成功 ${ok} / ${jobs.length}`);
        }
      } catch {
        // 瞬时失败重试，连续 5 次才停轮询（进度视图里给「重新查询」入口）
        d.pollFailures++;
        if (d.pollFailures >= 5) {
          this.stopExtractPolling();
          d.pollDead = true;
        }
      } finally {
        d.pollInFlight = false;
      }
    },

    startExtractPolling(id) {
      this.stopExtractPolling();
      if (!id) return; // 无任务号不轮询
      const d = this.extractDialog;
      d.pollInFlight = false;
      d.pollFailures = 0;
      d.pollDead = false;
      d.timer = setInterval(() => this.pollExtractOnce(id), 3000);
    },

    /** 查一次单任务（「重新查询」复用；防重入 + 隐藏页跳过） */
    async pollExtractOnce(id) {
      const d = this.extractDialog;
      if (!id) return; // 无任务号直接跳过
      if (d.pollInFlight || document.hidden) return;
      d.pollInFlight = true;
      try {
        const job = (await fetchDatasetJob(id)).data;
        d.job = job;
        d.pollFailures = 0;
        if (job.status === 'completed' || job.status === 'failed') {
          const remembered = this.recentJobs[d.objectName];
          if (remembered && remembered.id === job.id) delete this.recentJobs[d.objectName];
          this.stopExtractPolling();
          this.loadData({ silent: true }); // 轮询收尾：静默对齐列表
          if (job.status === 'completed' && d.jobType === 'extract') {
            this.$message.success(`已生成 ${job.image_count} 张，到批次状态芯片里选图后再导入标注平台`);
          } else if (job.status === 'completed') {
            this.$message.success('预标注任务已完成');
          }
        }
      } catch {
        // 瞬时失败重试，连续 5 次才停轮询（进度视图里给「重新查询」入口）
        d.pollFailures++;
        if (d.pollFailures >= 5) {
          this.stopExtractPolling();
          d.pollDead = true;
        }
      } finally {
        d.pollInFlight = false;
      }
    },

    /** 任务状态文案（进度视图的状态标签） */
    jobStatusText(status) {
      return { pending: '等待中', processing: '进行中', completed: '已完成', failed: '失败' }[status] || status || '';
    },

    /** 预标注进度弹窗的状态行（与批次弹窗共用同一份文案，避免两处口径不一） */
    preannotateLine(job) {
      return preAnnotateStatusText(job);
    },

    /** 任务状态色调（进度视图的状态标签） */
    jobStatusTag(status) {
      if (status === 'completed') return 'success';
      if (status === 'failed') return 'danger';
      if (status === 'processing') return 'primary';
      return 'info';
    },

    /** 进度视图里失败后回表单重推：补上表单所需的预览/类别/数据集选项 */
    async retryExtractFromProgress() {
      const d = this.extractDialog;
      d.progressOnly = false;
      this.loadExtractPreview(d.objectName);
      this.loadDatasetOptions();
      if (d.projects.length === 0) {
        try {
          d.projects = (await fetchLabelStudioProjects()).data;
        } catch {
          // 错误已由拦截器统一提示
        }
      }
    },

    /** 轮询停摆后手动恢复：清失败计数、立即查一次、重启轮询（单任务/批量都走这里） */
    requeryExtract() {
      const d = this.extractDialog;
      d.pollDead = false;
      d.pollFailures = 0;
      if (d.batchMode) {
        if (d.batchJobs.length > 0) {
          this.startBatchPolling(d.batchJobs);
          this.pollBatchOnce(d.batchJobs);
        }
      } else if (d.job && d.job.id) {
        this.startExtractPolling(d.job.id);
        this.pollExtractOnce(d.job.id);
      }
    },

    stopExtractPolling() {
      if (this.extractDialog.timer) {
        clearInterval(this.extractDialog.timer);
        this.extractDialog.timer = null;
      }
    },

    /**
     * 批次行轮询：在途批次每 BATCH_ROW_POLL_MS 重取一次列表（批次摘要随列表下发）。
     * 页面隐藏、上一次请求没回来、或用户正在弹窗里操作时都跳过本轮，
     * 既不叠请求，也不把展开行里的弹窗/选图刷掉。
     */
    startBatchRowPolling() {
      if (this.batchPollTimer) return;
      this.batchPollTimer = setInterval(() => {
        if (document.hidden || this.loading || this.pollHold) return;
        if (!this.hasBatchInFlight) {
          this.stopBatchRowPolling();
          return;
        }
        this.loadData({ silent: true });
      }, BATCH_ROW_POLL_MS);
    },

    stopBatchRowPolling() {
      if (this.batchPollTimer) {
        clearInterval(this.batchPollTimer);
        this.batchPollTimer = null;
      }
    },

    /** 待审核轮询与在途轮询互斥：在途时 5 秒那套已经会刷新列表 */
    maybeStartReviewRowPolling() {
      if (this.hasBatchInFlight || !this.hasBatchAwaiting) return;
      this.startReviewRowPolling();
    },

    /**
     * 待审核批次轮询：15 秒重取一次列表，让标注员提交后的「已审核」自己回流。
     * 后端 webhook + 30 秒增量同步先写回台账，页面这里只负责把它读回来。
     */
    startReviewRowPolling() {
      if (this.reviewPollTimer) return;
      this.reviewPollTimer = setInterval(() => {
        if (document.hidden || this.loading || this.pollHold) return;
        if (!this.hasBatchAwaiting || this.hasBatchInFlight) {
          this.stopReviewRowPolling();
          return;
        }
        this.loadData({ silent: true });
      }, REVIEW_ROW_POLL_MS);
    },

    stopReviewRowPolling() {
      if (this.reviewPollTimer) {
        clearInterval(this.reviewPollTimer);
        this.reviewPollTimer = null;
      }
    },

    /** 行内弹窗开关（批次详情/选图）：开着就暂停列表轮询，别把弹窗刷掉 */
    handlePipelineBusy(busy) {
      this.pipelineBusy = Math.max(0, this.pipelineBusy + (busy ? 1 : -1));
    },

    /** 加载标签字典 */
    async loadDictionary() {
      try {
        const res = await fetchLabelDictionary();
        this.tagGroups = res.data.groups;
      } catch {
        // 字典加载失败不影响主体功能
      }
    },

    /**
     * 拉列表。silent（轮询/任务终态触发）：不置全表遮罩、不清行勾选、在途就跳过本轮，
     * 用户能一边看一边干活；手动动作（搜索/翻页/增删）保持现状（要遮罩与清勾选）。
     */
    async loadData({ silent = false } = {}) {
      if (silent && this.loadInFlight) return; // 静默防叠：上一轮没回来就跳过
      this.loadInFlight = true;
      if (!silent) this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
        };

        if (this.filters.vin) params.vin = this.filters.vin;
        if (this.filters.type !== '' && this.filters.type !== null) {
          params.type = this.filters.type;
        }

        if (this.filters.record_date) params.record_date = this.filters.record_date;
        if (this.filters.object_name) params.object_name = this.filters.object_name;
        if (this.projectFilter) params.dataset_project = this.projectFilter;
        if (this.datasetStateFilter) params.dataset_state = this.datasetStateFilter;
        if (this.problemTagsFilter.length > 0) {
          params.problem_tags = this.problemTagsFilter.join(',');
        }

        for (const [key, val] of Object.entries(this.tagFilters)) {
          if (Array.isArray(val) && val.length > 0) {
            params[`tag_${key}`] = val.join(',');
          } else if (!Array.isArray(val) && val !== '' && val !== null) {
            params[`tag_${key}`] = val;
          }
        }

        const res = await fetchDataGroups(params, { silent });
        if (silent) {
          // 静默刷新：按 object_name 复用行对象，只覆盖属性。行对象不换，
          // 展开行里的子组件（批次芯片/选图弹窗）就复用实例，不会被卸载；
          // 数组换了 el-table 会清勾选，所以这里再按 object_name 补回来
          const selectedKeys = new Set(this.selectedRows.map(row => row.object_name));
          this.tableData = this.mergeRows(res.data.rows);
          if (selectedKeys.size > 0) this.restoreRowSelection(selectedKeys);
        } else {
          this.tableData = res.data.rows;
        }
        this.total = res.data.total;
        // 全局筛选项与标注平台入口随列表下发（与当前页无关，翻页时保持一致）
        if (Array.isArray(res.data.project_options)) this.projectOptions = res.data.project_options;
        if (res.data.ls_base_url) this.lsBaseUrl = res.data.ls_base_url;
        if (!silent) this.selectedFiles = {}; // 清空旧的展开行勾选（静默刷新保留，勾选不丢）
      } catch {
        // 静默失败保留上一轮数据（行内轮询自己会带错误提示），手动刷新才清空
        if (!silent) {
          this.tableData = [];
          this.total = 0;
        }
      } finally {
        this.loadInFlight = false;
        if (!silent) this.loading = false;
      }
    },

    /**
     * 静默刷新用：按 object_name 复用上一轮的行对象，只覆盖属性。
     * 行对象不换，展开行里的子组件（批次芯片/选图弹窗）就复用实例，不会被卸载重开。
     */
    mergeRows(rows) {
      const previous = new Map(this.tableData.map(row => [row.object_name, row]));
      return (rows || []).map(row => {
        const old = previous.get(row.object_name);
        if (!old) return row;
        Object.assign(old, row);
        return old;
      });
    },

    /** 静默刷新后补回行勾选：el-table 在 data 换新数组时会 clearSelection */
    restoreRowSelection(keys) {
      this.$nextTick(() => {
        const table = this.$refs.groupTable;
        if (!table) return;
        this.tableData.forEach(row => {
          if (keys.has(row.object_name)) table.toggleRowSelection(row, true);
        });
      });
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadData();
    },

    handleTagChange() {
      this.handleSearch();
    },

    /** 展开/收起标签筛选会顶掉表格高度，显式让表格重算内部滚动区 */
    toggleTagFilter() {
      this.showTagFilter = !this.showTagFilter;
      this.$nextTick(() => {
        const table = this.$refs.groupTable;
        if (table && table.doLayout) table.doLayout();
      });
    },

    /** 该目录对应的批次摘要（随列表行下发；没推送过则 null） */
    batchOf(row) {
      return (row && row.batch_summary) || null;
    },

    /**
     * 标注阶段（列标签只讲标注这条轴）：把后端下发的 batch_summary.stage（九档）
     * 投影成状态词表里的一个词（PIPELINE_STATES 单源，词面/色调都从表里取）。
     * 审完之后的去向三档（ready/in_set/removed）在这一列一律「已审核」；
     * 发布去向是另一条轴，只在操作列芯片里看，不在这里重复。
     */
    annotStateOf(row) {
      const b = this.batchOf(row);
      return b ? PIPELINE_STATES[b.stage] || null : null;
    },

    /** 工具栏跳转标注平台首页 */
    openLabelStudio() {
      if (this.lsBaseUrl) window.open(this.lsBaseUrl, '_blank');
    },

    handleReset() {
      this.filters = {
        vin: '',
        type: '',
        record_date: '',
        object_name: '',
      };
      this.tagFilters = {
        scene: '',
        weather: '',
        time: '',
        lighting: '',
        road: '',
        state: '',
        dynamic: [],
        static: [],
        anomaly: [],
      };
      this.problemTagsFilter = [];
      this.projectFilter = '';
      this.datasetStateFilter = '';
      this.pagination.page = 1;
      this.loadData();
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.page = 1;
      this.loadData();
    },

    handlePageChange(val) {
      this.pagination.page = val;
      this.loadData();
    },



    /** 记录日期时间展示：日期 + 时间拼接，缺时间只显示日期 */
    formatRecordTime(row) {
      if (!row.record_date) return '-';
      return row.record_time ? `${row.record_date} ${row.record_time}` : row.record_date;
    },

    /** 记录日期时间排序比较（字典序即时间序） */
    compareRecordTime(a, b) {
      return `${a.record_date} ${a.record_time}`.localeCompare(`${b.record_date} ${b.record_time}`);
    },

    /** 获取 URL：单文件返回 file 类型 URL（direct=true 为公开直连 URL，否则预签名）；文件夹返回当前网络通道的打包直链 */
    async fetchFileUrl(objectName, filePath, { direct = false } = {}) {
      // 按用户所选网络通道决定内网/公网地址
      const internal = getNetworkMode() === 'internal';

      const res = await fetchDownloadUrl(objectName, filePath, { internal, direct });
      return res.data || null;
    },

    /** 下载：数据组统一展示打包直链；单文件用 file-saver 保存 */
    handleDownload(objectName, filePath, fileName) {
      if (!filePath) {
        return this.openFolderDownloadDialog(objectName);
      }

      // 单文件：先取预签名 URL，再拉取 blob 保存
      const loading = this.$loading({
        lock: true,
        text: '正在获取下载链接...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      this.fetchFileUrl(objectName, filePath)
        .then(async data => {
          if (!data || !data.url) return;
          await downloadFromUrl(data.url, fileName);
        })
        .catch(() => {
          // 错误已由拦截器统一处理
        })
        .finally(() => loading.close());
    },

    /** 数据组下载：两种网络通道统一取直链并打开确认框 */
    async openFolderDownloadDialog(objectName) {
      if (this.directDownload.loading) return;
      this.directDownload.loading = true;
      try {
        const data = await this.fetchFileUrl(objectName);
        if (!data || !data.url) return;
        this.directDownload.internal = getNetworkMode() !== 'external';
        this.directDownload.objectName = objectName;
        this.directDownload.url = data.url;
        this.directDownload.visible = true;
      } finally {
        this.directDownload.loading = false;
      }
    },

    /** 复制数据组打包下载地址 */
    async copyDirectDownloadUrl() {
      if (!this.directDownload.url) return;
      await this.copyToClipboard(this.directDownload.url);
      this.$message.success('下载地址已复制，请粘贴到浏览器地址栏中打开');
    },

    /** 批量 zip 下载：manifest 中过滤出选中的文件 → 流式拼 zip 写盘 */
    async batchDownloadAsZip(groupRow) {
      const selected = this.selectedFiles[groupRow.object_name] || [];
      const selectedPaths = new Set(selected.map(f => f.file_path));
      const getFiles = async signal => {
        const res = await fetchDownloadManifest(groupRow.object_name, { signal });
        const manifest = res && res.data;
        if (!manifest || !manifest.files || manifest.files.length === 0) return null;
        // file_path 是完整对象路径，与 manifest 的 objectName 对应（name 是去前缀的相对路径）
        const subset = manifest.files.filter(f => selectedPaths.has(f.objectName));
        if (subset.length === 0) return null;
        return subset;
      };
      await this.$refs.zipDl.run(getFiles, `${groupRow.object_name.split('/').pop()}.zip`);
    },

    /** 复制单文件公开直连 URL（bucket 为 public） */
    async handleCopyLink(objectName, filePath) {
      const data = await this.fetchFileUrl(objectName, filePath, { direct: true });
      const url = data && data.url;
      if (!url) return;

      await this.copyToClipboard(url);
      this.$message.success('链接已复制到剪贴板');
    },

    /** 展开行文件（已过滤只显示 .pb） */
    pbFiles(groupRow) {
      return (groupRow.files || []).filter(f => this.isPbFile(f.file_name));
    },

    pbCount(groupRow) {
      return this.pbFiles(groupRow).length;
    },

    selectedCount(groupRow) {
      return (this.selectedFiles[groupRow.object_name] || []).length;
    },

    isFileSelected(groupRow, fileRow) {
      return (this.selectedFiles[groupRow.object_name] || [])
        .some(f => f.file_path === fileRow.file_path);
    },

    /** 全部勾选（勾选列只显示 .pb，勾选数即勾选的 pb 数） */
    isAllSelected(groupRow) {
      const n = this.selectedCount(groupRow);
      return n > 0 && n === this.pbFiles(groupRow).length;
    },

    /** 部分勾选（半选态） */
    isIndeterminate(groupRow) {
      const n = this.selectedCount(groupRow);
      return n > 0 && n < this.pbFiles(groupRow).length;
    },

    onToggleFile(groupRow, fileRow, checked) {
      const key = groupRow.object_name;
      const rows = (this.selectedFiles[key] || []).slice();
      const i = rows.findIndex(f => f.file_path === fileRow.file_path);
      if (checked && i < 0) rows.push(fileRow);
      if (!checked && i >= 0) rows.splice(i, 1);
      this.$set(this.selectedFiles, key, rows);
    },

    onToggleAll(groupRow, checked) {
      const key = groupRow.object_name;
      this.$set(this.selectedFiles, key, checked ? this.pbFiles(groupRow).slice() : []);
    },

    selectedCount(groupRow) {
      return (this.selectedFiles[groupRow.object_name] || []).length;
    },

    /** 批量下载：逐文件拿预签名 URL 并保存 */
    async batchDownload(groupRow) {
      const files = this.selectedFiles[groupRow.object_name] || [];
      if (!files.length) return;

      if (window.showSaveFilePicker) {
        // 打包 zip 主路径（与文件夹下载共用防连点）
        if (this.downloadingFolder) return;
        this.downloadingFolder = true;
        const done = () => { this.downloadingFolder = false; };
        this.batchDownloadAsZip(groupRow).then(done, done);
        return;
      }

      // 降级路径（无 showSaveFilePicker）：逐个文件直下，不拼 zip
      const loading = this.$loading({
        lock: true,
        text: `正在下载 ${files.length} 个文件...`,
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      try {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const data = await this.fetchFileUrl(groupRow.object_name, f.file_path);
          if (!data || !data.url) continue;
          if (data.type === 'file') {
            await downloadFromUrl(data.url, f.file_name);
          } else {
            window.open(data.url, '_blank');
          }
        }
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        loading.close();
      }
    },

    /** 批量复制链接：逐文件拿公开直连 URL，多行合并一次复制 */
    async batchCopyLink(groupRow) {
      const files = this.selectedFiles[groupRow.object_name] || [];
      if (!files.length) return;

      const loading = this.$loading({
        lock: true,
        text: '正在获取下载链接...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      try {
        const urls = [];
        for (const f of files) {
          const data = await this.fetchFileUrl(groupRow.object_name, f.file_path, { direct: true });
          if (data && data.url) urls.push(data.url);
        }
        if (!urls.length) return;
        await this.copyToClipboard(urls.join('\n'));
        this.$message.success(`已复制 ${urls.length} 个链接到剪贴板`);
      } finally {
        loading.close();
      }
    },

    /** 复制文本到剪贴板（HTTPS 下用 Clipboard API，否则降级 execCommand） */
    async copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    },

    /** 仅 .pb 文件支持 可视化（其他格式不显示按钮） */
    isPbFile(name) {
      return typeof name === 'string' && name.toLowerCase().endsWith('.pb');
    },

    /** 拉起 Node Studio 可视化：票据 → 带用户所选网络通道 → 跳转 nodestudio:// 协议 */
    async handleVisualize(row) {
      if (this.visualizeBusy) return;
      this.visualizeBusy = true;
      const loading = this.$loading({
        lock: true,
        text: '正在连接 Node Studio...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      try {
        const res = await fetchOpenTicket(row.id);
        const { ticket } = res.data;
        if (!ticket) return;
        await this._launchNodeStudio(ticket);
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        loading.close();
        // ticket 一次性，2 秒内防连点
        setTimeout(() => {
          this.visualizeBusy = false;
        }, 2000);
      }
    },

    /** 组装 Lichtblick 深链并新标签打开：选内外网入口 + 拼 layoutUrl（单文件/文件夹共用） */
    async _openLichtblick(dsParams, networkType) {
      if (!this.lichtblickConfig) {
        this.$message.warning('可视化入口配置未就绪，请刷新后重试');
        return;
      }
      const net = networkType || resolveNetworkType();
      // 内网可达时跳内网可视化入口，页面与文件传输均走内网
      const entryUrl =
        net === 'internal'
          ? this.lichtblickConfig.entryUrlInternal || this.lichtblickConfig.entryUrl
          : this.lichtblickConfig.entryUrl;
      // layoutUrl 相对路径时拼 entryUrl（同源免 CORS）
      const layoutUrl = this.lichtblickConfig.layoutUrl.startsWith('/')
        ? `${entryUrl.replace(/\/$/, '')}${this.lichtblickConfig.layoutUrl}`
        : this.lichtblickConfig.layoutUrl;
      const params = new URLSearchParams({ ds: 'remote-file', ...dsParams, layoutUrl });
      window.open(`${entryUrl}?${params.toString()}`, '_blank');
    },

    /** 浏览器中打开 Lichtblick 可视化：票据 → 30 分钟短时效 URL 深链（.pb 原生解析，零转换零缓存） */
    async handleLichtblickVisualize(row) {
      if (this.lichtblickBusy) return;
      this.lichtblickBusy = true;
      try {
        // 票据链路：ticket 一次性（5 分钟），兑换 URL 30 分钟有效
        const ticketRes = await fetchOpenTicket(row.id);
        const { ticket } = ticketRes.data;
        if (!ticket) return;
        // 网络类型同时决定 MinIO 端点与可视化入口
        const networkType = resolveNetworkType();
        const urlRes = await fetchStreamUrl(ticket, networkType);
        const { url } = urlRes.data;
        if (!url) return;
        await this._openLichtblick({ 'ds.url': url }, networkType);
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        setTimeout(() => {
          this.lichtblickBusy = false;
        }, 1000);
      }
    },

    /** 浏览器中打开 Lichtblick 文件夹可视化：会话短引用深链（清单存平台 30 分钟，Lichtblick 回查） */
    async handleFolderLichtblickVisualize(row) {
      if (this.lichtblickBusy) return;
      this.lichtblickBusy = true;
      try {
        const res = await fetchFolderSession(row.object_name);
        const { session_id } = res.data;
        if (!session_id) return;
        // 会话回查按 network_type 现签 URL：内网加速时直连内网 MinIO 端点
        const networkType = resolveNetworkType();
        // 会话回查 URL 用平台 origin（nginx 已放行 CORS），network_type 决定 MinIO 端点
        const sessionUrl = `${window.location.origin}/api/data/session/${session_id}?network_type=${networkType}`;
        await this._openLichtblick({ 'ds.sessionUrl': sessionUrl }, networkType);
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        setTimeout(() => {
          this.lichtblickBusy = false;
        }, 1000);
      }
    },

    /** 加载 Lichtblick 可视化入口配置（后端 config 下发） */
    async loadLichtblickConfig() {
      try {
        const res = await fetchLichtblickVisualizeConfig();
        this.lichtblickConfig = res.data;
      } catch {
        // 配置加载失败仅影响 LB 可视化按钮
      }
    },

    /** 文件夹级可视化：票据 → 拉起 Node Studio（打开文件夹内全部 .pb） */
    async handleFolderVisualize(row) {
      if (this.visualizeBusy) return;
      this.visualizeBusy = true;
      const loading = this.$loading({
        lock: true,
        text: '正在连接 Node Studio...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      try {
        const res = await fetchFolderTicket(row.object_name);
        const { ticket } = res.data;
        if (!ticket) return;
        await this._launchNodeStudio(ticket);
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        loading.close();
        // ticket 一次性，2 秒内防连点
        setTimeout(() => {
          this.visualizeBusy = false;
        }, 2000);
      }
    },

    /** 浏览器与 Node Studio 同机，探测结果经链接传给桌面端转发 */
    async _launchNodeStudio(ticket) {
      // health 服务随 deb 常驻：探测通直接拉起，不通跳下载页
      const probe = await this._probeNodeStudio();
      if (!probe.ok || probe.installed) {
        this._sendOpenLink(ticket);
        return;
      }
      window.location.hash = probe.docUrl;
    },

    /** 探测本机 Node Studio：后端给端口 → fetch 127.0.0.1:<port>/ping（800ms 超时） */
    async _probeNodeStudio() {
      let res;
      try {
        res = await fetchNodeStudioDownloadInfo();
      } catch {
        // 拿不到探测配置时按已装处理，不拦截
        return { ok: false, installed: true };
      }
      const { port, docUrl } = res.data;
      return { ok: true, installed: await this._ping(port), port, docUrl };
    },

    /** 构造 nodestudio:// 链接参数（ticket + 来源 + 用户所选网络通道） */
    _buildOpenLinkParams(ticket) {
      return new URLSearchParams({
        ticket,
        platform: window.location.origin,
        network_type: resolveNetworkType(),
      });
    },

    /** 正常拉起：location.href 跳协议（window.open 会被拦截） */
    async _sendOpenLink(ticket) {
      const params = await this._buildOpenLinkParams(ticket);
      window.location.href = `nodestudio://open?${params.toString()}`;
    },

    /** 单次探测 127.0.0.1:port/ping（800ms 超时，连接被拒/超时视为未运行） */
    async _ping(port) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 800);
      try {
        const resp = await fetch(`http://127.0.0.1:${port}/ping`, {
          signal: ctrl.signal,
          cache: 'no-store',
        });
        return resp.ok;
      } catch {
        return false;
      } finally {
        clearTimeout(timer);
      }
    },

    openUploadDialog() {
      this.uploadDialog.visible = true;
      this.uploadDialog.folderName = '';
      this.uploadDialog.folderNameValid = false;
      this.uploadDialog.folderError = '';
      this.uploadDialog.vin = '';
      this.uploadDialog.files = [];
      this.uploadDialog.uploading = false;
      this.uploadDialog.cancelling = false;
      this.uploadDialog.problemTags = [];
    },

    async handleFolderSelected() {
      const input = this.$refs.uploadFolderInput;
      if (!input || !input.files || input.files.length === 0) return;

      const files = Array.from(input.files);
      const firstPath = files[0].webkitRelativePath || files[0].name;
      const folderName = firstPath.split('/')[0];

      if (!FOLDER_NAME_PATTERN.test(folderName)) {
        this.uploadDialog.folderName = folderName;
        this.uploadDialog.folderNameValid = false;
        this.uploadDialog.folderError = '';
        this.uploadDialog.vin = '';
        this.uploadDialog.files = [];
        input.value = '';
        return;
      }

      const vinFile = files.find(f => {
        const relPath = f.webkitRelativePath || f.name;
        // 仅匹配根目录（不匹配子目录）
        return relPath === `${folderName}/veh_vin.txt`;
      });

      if (!vinFile) {
        this.uploadDialog.folderName = folderName;
        this.uploadDialog.folderNameValid = false;
        this.uploadDialog.folderError = '文件夹缺少 veh_vin.txt 文件（需放在文件夹根目录下）';
        this.uploadDialog.vin = '';
        this.uploadDialog.files = [];
        input.value = '';
        return;
      }

      const hasPbFile = files.some(f => {
        const relPath = f.webkitRelativePath || f.name;
        // 仅匹配根目录: {folderName}/{filename}.pb
        return relPath === `${folderName}/${f.name}` && f.name.toLowerCase().endsWith('.pb');
      });

      if (!hasPbFile) {
        this.uploadDialog.folderName = folderName;
        this.uploadDialog.folderNameValid = false;
        this.uploadDialog.folderError = '文件夹内必须包含 .pb 文件';
        this.uploadDialog.vin = '';
        this.uploadDialog.files = [];
        input.value = '';
        return;
      }

      let vin = '';
      try {
        const text = await vinFile.text();
        vin = text.trim();
      } catch {
        // 读取失败
      }

      this.uploadDialog.folderName = folderName;
      this.uploadDialog.folderNameValid = true;
      this.uploadDialog.folderError = '';
      this.uploadDialog.vin = vin;
      this.uploadDialog.files = files.map(file => ({
        name: file.name,
        relativePath: file.webkitRelativePath || file.name,
        size: file.size,
        raw: file,
        status: 'pending',
        progress: 0,
        error: '',
        xhr: null,
      }));

      input.value = '';
    },

    /** 关闭上传弹窗 — 上传中则确认取消并清理已上传文件 */
    async handleUploadDialogClose(done) {
      // Element UI before-close: done 函数用于实际关闭；直接设置 visible=false 时无 done
      const close = () => {
        if (typeof done === 'function') { done(); } else { this.uploadDialog.visible = false; }
      };

      if (this.uploadDialog.uploading && !this.uploadDialog.cancelling) {
        try {
          await this.$confirm(
            '上传正在进行中，取消将删除已上传到 MinIO 的文件，确定取消吗？',
            '确认取消上传',
            { confirmButtonText: '确定取消', cancelButtonText: '继续上传', type: 'warning' },
          );
        } catch {
          // 用户选择"继续上传"
          return;
        }
        await this.cancelUpload();
      }

      this.resetUploadDialog();
      close();
    },

    /** 取消上传：终止所有进行中的 XHR 并删除已上传的 MinIO 文件 */
    async cancelUpload() {
      this.uploadDialog.cancelling = true;

      // 1. 终止所有进行中的 XHR
      for (const fileInfo of this.uploadDialog.files) {
        if (fileInfo.xhr && fileInfo.status === 'uploading') {
          fileInfo.xhr.abort();
          fileInfo.status = 'error';
          fileInfo.error = '用户取消';
        }
      }

      // 2. 收集已成功上传的文件路径，调用后端批量删除
      const uploadedPaths = [];
      for (const fileInfo of this.uploadDialog.files) {
        if (fileInfo.status === 'success' && fileInfo._objectPath) {
          uploadedPaths.push(fileInfo._objectPath);
        }
      }

      if (uploadedPaths.length > 0) {
        try {
          await deleteObjects(uploadedPaths);
        } catch {
          // 清理失败不阻塞关闭
        }
      }

      this.uploadDialog.uploading = false;
    },

    async startUpload() {
      if (!this.canStartUpload) return;

      this.uploadDialog.uploading = true;

      // 按用户所选网络通道确定上传地址（内网/公网）
      const useInternal = getNetworkMode() === 'internal';

      // MinIO 对象路径保留完整相对路径: {folderName}/{subPath}
      const folderName = this.uploadDialog.folderName;
      const pendingFiles = this.uploadDialog.files.filter(f => f.status === 'pending');
      let lastUploadedFile = null;

      for (const fileInfo of pendingFiles) {
        // 上传过程中用户点了取消，终止后续文件
        if (this.uploadDialog.cancelling) break;

        // relativePath 形如 "000011_20260729_081626/subdir/file.bin"
        const objectPath = fileInfo.relativePath.startsWith(folderName + '/')
          ? fileInfo.relativePath
          : `${folderName}/${fileInfo.relativePath}`;
        await this.uploadSingleFile(fileInfo, objectPath, useInternal);

        if (fileInfo.status === 'success') {
          lastUploadedFile = fileInfo;
        }
      }

      // 被取消时不标记上传结束（cancelUpload 处理）
      if (this.uploadDialog.cancelling) return;

      // 全部成功才计算 SHA256 并入库
      const allSuccess = this.uploadDialog.files.every(f => f.status === 'success');
      if (allSuccess && this.uploadDialog.files.length > 0) {
        try {
          await this.uploadSHA256(lastUploadedFile, folderName, useInternal);
        } catch {
          this.$message.error('SHA256 标记上传失败');
        }

        // 入库：创建 data 表记录
        try {
          await this.batchCreateRecords(folderName);
        } catch {
          this.$message.error('数据入库失败');
        }

        this.$message.success(`全部 ${this.uploadDialog.files.length} 个文件上传成功`);
        this.loadData();
        this.uploadDialog.visible = false;
        this.resetUploadDialog();
      } else if (this.uploadDialog.files.some(f => f.status === 'success')) {
        this.$message.warning('部分文件上传失败，请检查失败项');
        this.uploadDialog.uploading = false;
      } else {
        this.uploadDialog.uploading = false;
      }
    },

    /** 计算最后一个文件的 SHA256 摘要并上传到 .sha256 标记文件 */
    async uploadSHA256(fileInfo, folderName, useInternal) {
      if (!fileInfo || !fileInfo.raw) return;

      // 1. 在浏览器中计算文件 SHA256
      const digest = await this.computeSHA256(fileInfo.raw);

      // 2. 获取 .sha256 文件的预签名上传 URL
      const res = await fetchUploadUrl(folderName, '.sha256', { internal: useInternal });
      const uploadUrl = res.data && res.data.url;
      if (!uploadUrl) throw new Error('获取 SHA256 上传链接失败');

      // 3. 直接 PUT SHA256 文本到 MinIO
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: digest,
        headers: { 'Content-Type': 'text/plain' },
      });

      if (!response.ok) {
        throw new Error(`SHA256 上传失败，状态码 ${response.status}`);
      }
    },

    /** 计算文件 SHA256 摘要（Web Crypto API） */
    async computeSHA256(file) {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    },

    /** 上传文件注册到 MySQL data 表 */
    async batchCreateRecords(folderName) {
      const files = this.uploadDialog.files.map(f => ({
        // MinIO 对象路径带 v3/ 前缀，需与 object_name 一致
        file_path: `v3/${f.relativePath}`,
        file_name: f.name,
        object_name: `v3/${folderName}`,
        vin: this.uploadDialog.vin,
      }));

      const problemTags = this.uploadDialog.problemTags.join(',');
      await fetchBatchCreate(files, problemTags);
    },

    /** 上传单个文件到 MinIO（浏览器直传，不经过后端） */
    async uploadSingleFile(fileInfo, objectPath, useInternal) {
      fileInfo.status = 'uploading';
      fileInfo.progress = 0;

      try {
        const slashIdx = objectPath.indexOf('/');
        const objectName = objectPath.substring(0, slashIdx);
        const fileName = objectPath.substring(slashIdx + 1);

        // 1. 获取预签名上传 URL
        const res = await fetchUploadUrl(objectName, fileName, { internal: useInternal });
        const uploadUrl = res.data && res.data.url;
        if (!uploadUrl) {
          throw new Error('获取上传链接失败');
        }

        // 获取 URL 期间用户点了取消则跳过上传
        if (this.uploadDialog.cancelling) throw new Error('用户取消');

        // 2. 通过 XMLHttpRequest 直传文件到 MinIO（支持进度回调和取消）
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          fileInfo.xhr = xhr;
          xhr.open('PUT', uploadUrl, true);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              fileInfo.progress = Math.round((event.loaded / event.total) * 100);
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`MinIO 返回状态码 ${xhr.status}`));
            }
          };

          xhr.onerror = () => reject(new Error('网络错误，上传失败'));
          xhr.ontimeout = () => reject(new Error('上传超时'));
          xhr.onabort = () => reject(new Error('用户取消'));
          xhr.timeout = 300000;

          xhr.send(fileInfo.raw);
        });

        fileInfo.status = 'success';
        fileInfo.progress = 100;
        fileInfo._objectPath = objectPath; // 记录路径，用于取消时清理
      } catch (err) {
        // 用户取消不显示错误提示（cancelUpload 统一处理）
        if (err.message !== '用户取消') {
          fileInfo.status = 'error';
          fileInfo.error = err.message;
          this.$message.error(`${fileInfo.name}: ${err.message}`);
        }
      } finally {
        fileInfo.xhr = null;
      }
    },

    resetUploadDialog() {
      this.uploadDialog.folderName = '';
      this.uploadDialog.folderNameValid = false;
      this.uploadDialog.folderError = '';
      this.uploadDialog.vin = '';
      this.uploadDialog.files = [];
      this.uploadDialog.uploading = false;
      this.uploadDialog.cancelling = false;
      this.uploadDialog.problemTags = [];
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(1)} ${units[i]}`;
    },

    /** 是否为事件数据的事件名（error name）标签 */
    isTriggerTag(row, tag) {
      return (row.trigger_name_cn || row.trigger_name) === tag;
    },

    openEditDialog(row) {
      this.editDialog.objectName = row.object_name;
      // trigger_name 是后端注入的固有属性，不参与编辑与保存
      const triggerTag = row.trigger_name_cn || row.trigger_name;
      this.editDialog.problemTags = (row.problem_tags || []).filter(tag => tag !== triggerTag);
      this.editDialog.visible = true;
    },

    async saveProblemTags() {
      this.editDialog.saving = true;
      try {
        const tags = this.editDialog.problemTags.join(',');
        await updateProblemTags(this.editDialog.objectName, tags);
        this.$message.success('问题分类更新成功');
        this.editDialog.visible = false;
        this.loadData();
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        this.editDialog.saving = false;
      }
    },

    /** 删除数据分组（MySQL + PostgreSQL + MinIO） */
    async handleDelete(row) {
      const objectName = row.object_name;
      const fileCount = row.file_count || 0;

      let password = '';
      try {
        const { value } = await this.$prompt(
          `确定要删除文件夹「${objectName}」吗？此操作不可恢复！\n\n请输入删除密码以确认：`,
          '确认删除',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            inputType: 'password',
            inputPlaceholder: '请输入删除密码',
            confirmButtonClass: 'el-button--danger',
          },
        );
        password = value;
      } catch {
        return; // 用户取消
      }

      if (!password) {
        this.$message.warning('请输入删除密码');
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: '正在删除数据...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });

      try {
        await deleteDataGroup(objectName, password);
        this.$message.success(`已删除「${objectName}」`);
        // 当前页被删空时回到上一页
        if (this.tableData.length === 1 && this.pagination.page > 1) {
          this.pagination.page--;
        }
        this.loadData();
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        loading.close();
      }
    },
  },
};
</script>

<style scoped>
/* 页面撑满 main 区：顶栏与筛选卡固定，剩余高度全给表格卡 */
.data-list {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.filter-card {
  flex: 0 0 auto;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.filter-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 4px 16px;
}

.filter-form .el-form-item {
  margin-bottom: 0;
}

.filter-form .el-form-item .el-input,
.filter-form .el-form-item .el-select,
.filter-form .el-form-item .el-date-editor {
  width: 100%;
}

.table-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.table-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 卡片内容区接管剩余高度：表格自适应，分页始终贴底 */
.table-card ::v-deep .el-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-card ::v-deep .el-card__body > .el-table {
  flex: 1 1 0;
  min-height: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.total-count {
  color: #909399;
  font-size: 13px;
  font-weight: 400;
}

.pagination-wrap {
  flex: 0 0 auto;
  margin-top: 12px;
  text-align: right;
}

/* 展开区域 */
.expand-inner {
  padding: 12px 20px 4px;
}

.expand-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.expand-toolbar-count {
  margin-right: 12px;
  font-size: 12px;
  color: #909399;
}

/* 统计列 */
.file-count {
  font-weight: 600;
  color: #409EFF;
}

/* 标签列 */
.label-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
  align-items: center;
}

/* 标签权重 */
.tag-weight {
  margin-left: 3px;
  font-size: 11px;
}

/* 列头口径说明图标 */
.header-hint {
  margin-left: 4px;
  color: #909399;
  font-size: 12px;
  cursor: help;
  vertical-align: middle;
}

/* 无标注状态 */
.no-label-status {
  color: #c0c4cc;
}

/* 操作列：按钮与状态 chip 同流换行 */
.op-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.op-cell .pipe-cell {
  margin-left: 6px;
}

/* 危险按钮分隔线 */
.op-divider {
  width: 1px;
  height: 14px;
  margin: 0 6px;
  background: #dcdfe6;
}

.danger-text {
  color: #f56c6c;
}

/* 问题分类单元格（可编辑） */
.problem-tags-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.problem-tags-cell .label-tags {
  flex: 1;
  width: 100%;
  max-width: 100%;
}

/* 问题分类标签：最长显示到单元格宽度，超出用省略号截断 */
.problem-tags-cell .label-tags .trigger-popover {
  min-width: 0;
  max-width: 100%;
}

.problem-tags-cell .label-tags .el-tag {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: normal;
  overflow-wrap: normal;
  height: auto;
  line-height: 1.4;
  min-width: 0;
  max-width: 100%;
}

/* 事件名 popover 展示全部文案 */
.problem-tag-popover {
  max-width: 360px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.edit-tags-btn {
  flex-shrink: 0;
  padding: 2px 4px;
  font-size: 14px;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.problem-tags-cell:hover .edit-tags-btn {
  opacity: 1;
}

/* 编辑问题分类弹窗 */
.edit-dialog-body {
  max-height: 420px;
  overflow-y: auto;
}

/* 标签筛选区域 */
.tag-filter-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.tag-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
  user-select: none;
}

.tag-filter-toggle:hover {
  color: #66b1ff;
}

.tag-filter-badge {
  color: #e6a23c;
  font-weight: 500;
}

.tag-filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  margin-top: 12px;
}

.tag-filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tag-filter-label {
  font-size: 12px;
  color: #909399;
  line-height: 1;
}

.tag-filter-item .el-select {
  width: 100%;
}

/* 表格头部右侧 */
.table-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 上传弹窗 — 左右分栏 */
.upload-dialog-body {
  display: flex;
  gap: 24px;
  min-height: 360px;
}

.upload-left {
  flex: 1;
  min-width: 0;
}

.upload-right {
  width: 340px;
  flex-shrink: 0;
  padding-left: 24px;
  border-left: 1px solid #ebeef5;
  overflow-y: auto;
  max-height: 420px;
}

.upload-section {
  margin-bottom: 16px;
}

.upload-section:last-child {
  margin-bottom: 0;
}

.upload-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 6px;
}

.upload-section-count {
  margin-left: auto;
  font-weight: 400;
  font-size: 12px;
  color: #909399;
}

.upload-folder-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-family: monospace;
  margin-left: 8px;
}

.upload-file-input {
  display: none;
}

.upload-file-hint {
  color: #c0c4cc;
  font-size: 13px;
  margin-left: 8px;
}

.upload-folder-error {
  margin-top: 8px;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.6;
}

.upload-folder-error code {
  background: #fef0f0;
  padding: 1px 4px;
  border-radius: 3px;
}

.upload-folder-ok {
  margin-top: 8px;
  color: #67c23a;
  font-size: 12px;
}

.upload-folder-vin {
  color: #409eff;
  font-family: monospace;
}

.upload-file-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  width: 100%;
}

.upload-file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-bottom: 1px solid #f2f2f2;
  font-size: 12px;
}

.upload-file-item:last-child {
  border-bottom: none;
}

.upload-file-name {
  flex: 1;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-file-size {
  color: #909399;
  flex-shrink: 0;
}

.upload-file-status {
  flex-shrink: 0;
}

.upload-file-status.pending {
  color: #909399;
}

.upload-file-status.success {
  color: #67c23a;
}

.upload-file-status.error {
  color: #f56c6c;
  cursor: help;
}

/* 问题分类分组 */
.problem-group {
  margin-bottom: 12px;
}

.problem-group:last-child {
  margin-bottom: 0;
}

.problem-group-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
  padding-left: 2px;
}

.problem-group-items {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 6px;
}

.problem-group-items .el-checkbox {
  margin-right: 0;
  margin-left: 0;
}

.problem-group-items .el-checkbox.is-bordered {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 4px;
  height: 30px;
  margin: 0;
  box-sizing: border-box;
}

.problem-group-items .el-checkbox.is-bordered .el-checkbox__label {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
}

.extract-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.publish-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.extract-batch-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.extract-batch-name {
  font-family: monospace;
  font-size: 12px;
}
.extract-error {
  margin-top: 6px;
  color: #f56c6c;
  font-size: 12px;
  word-break: break-all;
}
/* 进度视图骨架：类型标题 + 目标 + 状态标签（图/预标注/发布三处同构） */
.extract-progress-title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.extract-progress-target {
  margin: 0 8px;
  font-weight: 400;
  color: #909399;
  font-family: monospace;
}
/* 轮询停摆提示 + 重新查询 */
.extract-poll-dead {
  margin-top: 8px;
  font-size: 12px;
  color: #e6a23c;
}
.extract-filmstrip {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.extract-film-item {
  flex: 0 0 auto;
  width: 84px;
  cursor: pointer;
  position: relative;
}
.extract-film-check {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 11px;
}
/* 胶片条放大查看：弹窗宽度由脚本按关键帧比例算，放映区只负责贴合 */
.fv-head { display: flex; align-items: baseline; gap: 10px; }
.fv-head-main { font-size: 15px; font-weight: 600; color: #303133; }
.fv-head-sub { font-size: 12px; color: #909399; font-weight: 400; }
.fv-body { padding: 0 0 4px; }
.fv-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0d0f13;
  border-radius: 8px;
  overflow: hidden;
}
.fv-img { max-width: 100%; max-height: 100%; display: block; }
.fv-empty { color: #909399; font-size: 13px; }
.fv-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, .16);
  color: #fff;
  font-size: 18px;
  line-height: 38px;
  cursor: pointer;
  opacity: .38;
  transition: opacity .18s, background .18s;
}
.fv-stage:hover .fv-nav { opacity: 1; }
.fv-nav:hover { opacity: 1; }
.fv-nav:hover { background: rgba(255, 255, 255, .3); }
.fv-nav-prev { left: 12px; }
.fv-nav-next { right: 12px; }
.fv-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 26px 14px 10px;
  background: linear-gradient(to top, rgba(0, 0, 0, .62), rgba(0, 0, 0, 0));
  color: #fff;
  font-size: 12px;
  pointer-events: none;
}
.fv-caption-time { font-size: 14px; font-weight: 600; letter-spacing: .5px; }
.fv-caption-state { color: rgba(255, 255, 255, .72); }
.fv-caption-state.on { color: #8CC5FF; }
.fv-strip {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-bottom: 2px;
  overflow-x: auto;
  scrollbar-width: thin;
}
.fv-thumb {
  position: relative;
  flex: none;
  width: 88px;
  height: 50px;
  border: 2px solid transparent;
  border-radius: 5px;
  overflow: hidden;
  background: #f2f3f5;
  cursor: pointer;
}
.fv-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fv-thumb.on { border-color: #409EFF; }
.fv-thumb.active { box-shadow: 0 0 0 2px rgba(64, 158, 255, .35); }
.fv-thumb-check {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 15px;
  height: 15px;
  line-height: 15px;
  text-align: center;
  border-radius: 50%;
  background: #409EFF;
  color: #fff;
  font-size: 10px;
}
.fv-thumb-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #C0C4CC;
  font-size: 12px;
}
.fv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}
.fv-tip { color: #A8ABB2; font-size: 12px; }
.fv-actions { display: flex; align-items: center; gap: 8px; }
.fv-actions .el-button + .el-button { margin-left: 0; }
.extract-film-img {
  width: 84px;
  height: 56px;
  object-fit: cover;
  display: block;
  border: 2px solid transparent;
  border-radius: 3px;
  box-sizing: border-box;
  background: #f5f7fa;
}
.extract-film-item.selected .extract-film-img {
  border-color: #409eff;
}
.extract-film-empty {
  width: 84px;
  height: 56px;
  background: #f5f7fa;
  border: 2px solid transparent;
  border-radius: 3px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
}
.extract-film-item.selected .extract-film-empty {
  border-color: #409eff;
}
.extract-film-label {
  font-size: 11px;
  color: #909399;
  text-align: center;
  margin-top: 2px;
  white-space: nowrap;
}

/* 生成标注数据弹窗：760px 重排（项目/数据集一行，范围-参数-输出三段） */
.xf { font-size: 13px; }
.xf-top { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; margin-bottom: 14px; }
.xf-field { display: flex; align-items: center; gap: 8px; }
.xf-label { color: #606266; font-size: 12px; flex: none; }
.xf-project { width: 280px; }
.xf-section { border-top: 1px solid #EBEEF5; padding: 12px 0 10px; }
.xf-section-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.xf-step {
  width: 18px; height: 18px; border-radius: 50%; background: #409EFF; color: #fff;
  font-size: 11px; display: inline-flex; align-items: center; justify-content: center; flex: none;
}
.xf-count { color: #909399; font-size: 12px; }
.xf-tools { margin-left: auto; display: flex; gap: 2px; }
.xf-films { display: flex; flex-wrap: wrap; gap: 8px; max-height: 176px; overflow-y: auto; }
.xf-film {
  position: relative; width: 96px; cursor: pointer; border: 2px solid #EBEEF5;
  border-radius: 4px; overflow: hidden; background: #F5F7FA;
}
.xf-film img { width: 100%; height: 60px; object-fit: cover; display: block; }
.xf-film.on { border-color: #409EFF; }
.xf-film-name {
  display: block; font-size: 11px; color: #909399; padding: 2px 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.xf-film-check {
  position: absolute; top: 2px; right: 2px; background: #409EFF; color: #fff;
  font-size: 11px; line-height: 14px; padding: 0 3px; border-radius: 2px;
}
.xf-film-empty {
  height: 60px; display: flex; align-items: center; justify-content: center;
  color: #C0C4CC; font-size: 12px;
}
.xf-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.xf-row:last-child { margin-bottom: 0; }
.xf-hint { color: #909399; font-size: 12px; line-height: 18px; }
.xf-error { color: #F56C6C; }

/* 内网打包下载确认框 */
.direct-download-intro {
  padding: 12px 14px;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  background: #f4f9ff;
}
.direct-download-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}
.direct-download-heading i {
  color: #409eff;
  font-size: 17px;
}
.direct-download-intro p {
  margin-top: 6px;
  color: #606266;
  font-size: 13px;
  line-height: 20px;
}
.direct-download-intro p.direct-download-object {
  color: #909399;
  font-size: 12px;
}
.direct-download-label {
  margin: 18px 0 8px;
  color: #606266;
  font-size: 13px;
}
.direct-download-url :deep(.el-input__inner) {
  color: #303133;
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
}
.direct-download-guide {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}
.direct-download-guide i {
  flex: none;
  margin-top: 2px;
  color: #e6a23c;
}
</style>

<!-- 放行 wrapper 点击事件：下载进度弹窗期间页面可继续操作 -->
<style>
.el-dialog__wrapper {
  pointer-events: none;
}
.el-dialog__wrapper .el-dialog {
  pointer-events: auto;
}
/* 胶片条全屏蒙版：dialog 内部元素是 Element 渲染的，不带组件 scoped 属性，必须放全局块 */
.film-viewer-dialog {
  border-radius: 10px;
  overflow: hidden;
}
.film-viewer-dialog .el-dialog__header {
  padding: 14px 20px 10px;
}
.film-viewer-dialog .el-dialog__body {
  padding: 0 20px 14px;
}
</style>
